import { Component, OnInit, OnDestroy, AfterContentInit, ViewChild, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TransformerErrorAnalysis, TransformerErrorDialogComponent } from 'src/app/views/shared/transformer-error-dialog/transformer-error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnFilter } from 'src/app/domainObjects/columnFilter';
import { ColumnMapDefinition } from 'src/app/domainObjects/columnMapDefinition';
import { DataConnectorMapping } from 'src/app/domainObjects/dataConnectorMapping';
import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { PreviewDataOptions } from 'src/app/domainObjects/previewDataOptions';
import { GetCacheKeyRequest, GetDataFarmPreviewRequest } from 'src/app/messaging/getDataFarmPreviewRequest';
import { RowExtractorPreviewDataResponse } from 'src/app/messaging/rowExtractorPreviewDataResponse';
import { RunConfigRequest, RunCurrentConfigRequest } from 'src/app/messaging/runConfigRequest';
import { Rule } from 'src/app/rule';
import { ConfigService } from 'src/app/services/config/config.service';
import { ConfigViewModelService } from 'src/app/services/configViewModel/config-view-model.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { TransformPipelineService } from 'src/app/services/transformPipeline/transform-pipeline.service';
import { ColumnInfo, PreviewTableComponent } from 'src/app/views/preview-table/preview-table.component';
import { SaveMappingConfigDialogComponent } from 'src/app/views/save-mapping-config-dialog/save-mapping-config-dialog.component';
import { TransformerViewModel } from 'src/app/views/transformer-list/transformer-list.component';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformPipelineDialogComponent } from 'src/app/views/transform/transform-pipeline-dialog/transform-pipeline-dialog.component';
import { GroupedTransformPipelineItem, TransformPipelineItem, TransformPipelineViewModel } from 'src/app/views/transform/transform-pipeline-dialog/transformPipelineViewModel';
import { BaseComponent } from '../../../../../base/base.component';
import { TransformDetailViewModelBase } from './transformDetailViewModelBase';
import { DataFarmViewModel } from 'src/app/viewModels/dataFarmViewModel';
import { Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { CopilotContextService } from 'src/app/copilot/services/copilot-context.service';
import { CopilotService } from 'src/app/copilot/services/copilot.service';
import { ColumnFilterInfo } from 'src/app/views/previewTable/filterUI/column-filter/column-filter.component';
import { TransformerParametersComponent } from 'src/app/views/transform/transformerDetail/transformer-parameters/transformer-parameters.component';
import { UntypedFormGroup } from '@angular/forms';
import { FieldValidationErrorViewComponent } from 'src/app/views/shared/fieldValidation/field-validation-error-view/field-validation-error-view.component';
import { Edge } from '@swimlane/ngx-graph';
import { UserDataService } from 'src/app/services/user-data-service';
import { RefineryPresenceService } from 'src/app/services/refinery-presence.service';
import { CacheKeyType, PreviewType, TransformType, TransformTypeUtil } from 'src/app/domainObjects/transformType';
import { ExportOutputDataComponent } from 'src/app/views/transform/export-output-data/export-output-data.component';
import { DataRefineryConfig } from 'src/app/domainObjects/dataRefineryConfig';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { SortInfo } from 'src/app/domainObjects/sortInfo';
import { OptionItem, OptionItemDataType } from 'src/app/model/optionItem';
import { InputPrvwFilterViewModel } from 'src/app/viewModels/inputPrvwFilterViewModel';
import { GetSummaryDataFarmPreviewRequest, SummaryType } from 'src/app/messaging/getSummaryDataFarmPreviewRequest';
import { MatMenuTrigger } from '@angular/material/menu';
import { SelectFilterInfo, ValuesColor } from 'src/app/views/shared/select-filter-option/select-filter-option.component';
import { PreviewData } from 'src/app/domainObjects/previewData';
import { GetFilteredselcolvalsRequest } from 'src/app/messaging/getFilteredselcolvalsRequest';
import { ComponentCanDeactivate } from 'src/app/guards/pending-changes.guard';
import { isEqual } from 'lodash';
import { DataDictionaryType } from 'src/app/domainObjects/dataDictionaryType';
import { DataDictionaryItem, DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { ExportColumnInfo, ExportViewModel } from 'src/app/viewModels/exportViewModel';
import { FarmConverterTransformPreviewDataResponse } from 'src/app/messaging/farmConverterTransformPreviewDataResponse';
import { PreviewDataImportResponse } from 'src/app/messaging/previewDataResponse';
import { NodeRunStatus } from 'src/app/domainObjects/nodeRunStatus';
import { CacheStatusRequest, ResetNodeStatusRequest } from 'src/app/messaging/resetRefineryNodesStatusRequest';
import { ColoredTextFindReplaceItem, ColoredTextItem } from 'src/app/domainObjects/coloredTextItem';
import { ContainsTextDialogOptionComponent } from 'src/app/views/shared/contains-text-dialog-option/contains-text-dialog-option.component';
import { FindReplaceChooserMapDialogOptionComponent } from 'src/app/views/shared/find-replace-chooser-map-dialog-option/find-replace-chooser-map-dialog-option.component';
import { RunrefineryResponse } from 'src/app/model/RunrefineryResponse';
import { Exception } from 'src/app/exceptions/exception';

/** Transform types with a real compiled engine -- the single source of truth
 * for whether a Results Dashboard should show a "Run Time (when compiled)"
 * box at all, instead of each detail template keeping its own hardcoded
 * guess (the Architect, 2026-09-30, extending the same pattern already
 * proven for the Audit button's own compile-capable list -- see
 * transformer-top-header.component.ts's _auditableTransformTypes). Seeded
 * with today's known types for graceful behavior before the live answer
 * arrives, then replaced by the authoritative list from
 * compiled_blueprint._SUPPORTED_TRANSFORM_TYPES on the server. Fetched once
 * and shared by every instance, not re-fetched per transformer. Deliberately
 * separate from the audit-capable list: compile-capable and audit-adapter-
 * capable are different capabilities that happen to be the same two types
 * today but aren't guaranteed to stay that way. */
let _compilableTransformTypes = new Set<number>([0, 7]);
let _compilableTypesFetchStarted = false;

@Component({
    selector: 'app-base-transformer-ui',
    templateUrl: './base-transformer-ui.component.html',
    styleUrls: ['./base-transformer-ui.component.css'],
    standalone: false
})
export class BaseTransformerUIComponent<TViewModel extends TransformDetailViewModelBase<any>> extends BaseComponent
  implements ComponentCanDeactivate, OnInit, OnDestroy, AfterContentInit {

  // ── Copilot integration ────────────────────────────────────────────────────
  // Injected without touching the constructor chain so no subclass needs changing.
  private readonly _copilotCtx      = inject(CopilotContextService);
  protected readonly _copilotSvc    = inject(CopilotService);
  private _copilotEventSub: Subscription;
  private _rulesSortedSub: Subscription;
  private _copilotAnalysisSub: Subscription;
  private _copilotNavSub: Subscription;
  private _copilotAuditSub: Subscription;
  private _copilotBpmScoreSub: Subscription;
  private _sampleRangeUpdateSub: Subscription;
  private _configVmSub: Subscription;
  protected _copilotSessionId: string | null = null;
  protected _dtrRunning = false;
  private _dtrRunComplete$ = new Subject<void>();
  private _copilotFilterValuesCache: Map<string, any[]> = new Map();
  protected _auditFilterValuesCache:  Map<string, any[]> = new Map();

  /** True whenever THIS tab does not currently hold the Edit lock for the
   * open refinery — bound into <app-transformer-top-header>'s isReadOnly
   * input to disable Save/Run/Reset/Paste/Import there. The backend
   * independently rejects the underlying calls a Read-Only tab isn't
   * authorized for; this is the client-side half. */
  // 'none' means "no Edit lock has been contested for this refinery in this
  // tab yet" — e.g. a brand-new, just-created refinery whose first node is
  // being opened right after creation, before anything ever went through
  // NodedefinitionComponent.tryAcquireEdit(). That must NOT default to
  // Read-Only (mode !== 'edit' would), or creating a refinery and
  // immediately opening its first node would incorrectly show Read-Only.
  // Matches NodedefinitionComponent's own isReadOnly definition exactly —
  // only an explicit 'readOnly' (denied or lost) counts.
  get isReadOnly(): boolean {
    return this.refineryPresence.mode === 'readOnly';
  }

  /**
   * Return the numeric transform type for Copilot, or null to disable Copilot
   * for this transformer. Override in subclasses that support Copilot.
   */
  protected getCopilotTransformType(): number | null { return null; }

  /** Column names to expose to the Copilot. Override in subclasses. */
  protected getCopilotColumnNames(): string[] { return []; }

  /**
   * Cockpit constraints — the complete set of valid values the UI exposes for this transformer.
   * Override in each transformer subclass that supports Copilot. Return an object that captures
   * every restriction the UI enforces: valid enum options, valid source/target columns from the DD,
   * conditional availability rules, default values, etc.
   * The Copilot must stay within these bounds — it is the same cockpit the analyst uses.
   */
  protected getCopilotConstraints(): any { return {}; }

  /**
   * Push updated constraints to the Copilot context.
   * Call this from subclasses after async data loads complete (e.g. after
   * optionsTarget is populated) so the Copilot receives the full constraint set.
   */
  protected refreshCopilotConstraints(): void {
    const transformType = this.getCopilotTransformType();
    if (transformType === null) return;
    this._copilotCtx.update({ constraints: this.getCopilotConstraints() });
  }

  /** Current transformer config to expose to the Copilot. Override in subclasses. */
  protected getCopilotConfig(): any { return {}; }

  /** Whether Run All Rows is active — used by the audit to decide confirmation dialog. */
  protected getAuditRunAll(): boolean { return false; }

  /** Called when the Copilot panel "Run Audit" button is clicked (Flavor 1).
   *  Override in subclasses that have the Audit Dashboard. */
  protected _triggerAuditRun(): void {}

  /**
   * Apply a Copilot-sorted rule list. Called after Flask re-orders rules by actual hit counts.
   * Override in subclasses that have a sortable rule list (e.g. Row Extractor).
   * Must update the view model WITHOUT triggering a DTR run.
   * Do NOT add a _skipNextSortedRules guard here — the base subscription handler owns that.
   */
  protected applySortedRules(_rules: any[]): void {}

  /**
   * Run stats to push to the Copilot after every transformer run.
   * Override in subclasses to return the actual stats from the view model.
   * IMPORTANT: ruleIndex values MUST be 1-based positional (use `i + 1`, never `r.index`).
   * _resort_by_actual_hits on the Flask side subtracts 1 to align with 0-based config indices;
   * passing 0-based values shifts the entire hit map by -1 and produces wrong sort decisions.
   */
  protected getCopilotRunStats(): any { return {}; }

  /**
   * Called when the Copilot proposes a config change.
   * Override in subclasses to apply the incoming config to the view model.
   */
  protected applyCopilotConfig(_config: any, _mode: 'replace' | 'merge' | 'preview', _skipRun?: boolean): void { }

  /**
   * Run the DTR as a Copilot pre-run to populate fresh run stats before analysis.
   * Override in subclasses to set transform-specific flags (e.g. runAll = true).
   */
  protected async _runForCopilotAnalysis(): Promise<void> {
    this._isCopilotRun = true;
    try {
      await this.runCurrentTransform(undefined, undefined, false);
    } finally {
      this._isCopilotRun = false;
    }
    const runStats = this.getCopilotRunStats();
    if (runStats && Object.keys(runStats).length > 0) {
      this._copilotCtx.update({ lastRunStats: runStats, currentConfig: this.getCopilotConfig() });
    }
  }
  // ──────────────────────────────────────────────────────────────────────────
  
  @ViewChild('table') table: MatTable<Rule> | undefined;
  @ViewChild('exportOutputData') exportOutputData: ExportOutputDataComponent;
  /** Type-based query (no template ref name needed) -- every transform type's own
   *  detail template already has exactly one <app-transformer-parameters>, so this
   *  resolves generically for all of them. Promoted from extractor-transform-detail
   *  (the Architect, 2026-09-27) so resetTransformer() below can reuse it for any type. */
  @ViewChild(TransformerParametersComponent) parametersComponent: TransformerParametersComponent;

  @ViewChild('previewTable') previewTable: PreviewTableComponent;
  @ViewChild('copilotPreviewTable') copilotPreviewTable: PreviewTableComponent;
  @ViewChild('auditPreviewTable')   auditPreviewTable:   PreviewTableComponent;
  @ViewChild('inputDataFarmPreviewTable') inputDataFarmPreviewTable: PreviewTableComponent;
  @ViewChild('outputDataFarmPreviewTable') outputDataFarmPreviewTable: PreviewTableComponent;
  @ViewChild('lookupDataFarmPreviewTable') lookupDataFarmPreviewTable: PreviewTableComponent;
  @ViewChild('previewTableA') previewTableA: PreviewTableComponent;
  @ViewChild('previewTableB') previewTableB: PreviewTableComponent;
  @ViewChild('previewTableC') previewTableC: PreviewTableComponent;
  @ViewChild('previewTableD') previewTableD: PreviewTableComponent;
  @ViewChild('previewTableE') previewTableE: PreviewTableComponent;

  @ViewChild('summaryDataFarmPreviewTableResult') summaryDataFarmPreviewTableResult: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableInput') summaryDataFarmPreviewTableInput: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableOutput') summaryDataFarmPreviewTableOutput: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableLookup') summaryDataFarmPreviewTableLookup: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableA') summaryDataFarmPreviewTableA: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableB') summaryDataFarmPreviewTableB: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableC') summaryDataFarmPreviewTableC: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableD') summaryDataFarmPreviewTableD: PreviewTableComponent;
  @ViewChild('summaryDataFarmPreviewTableE') summaryDataFarmPreviewTableE: PreviewTableComponent;

  transformViewModel: TransformerViewModel;
  dataTransformConfigViewModel: DataTransformConfigViewModel;
  viewModel: TViewModel;
  /** Set during a Copilot-triggered run to prevent runCurrentTransform from
   *  writing the live view model back into transformConfig. The analyst owns Save. */
  protected _isCopilotRun = false;
  protected _isCopilotPreviewRun = false;
  /** One-shot flag: suppresses the next applySortedRules call after a Copilot run.
   *  The simulation-based pre-sort is already optimal; a post-run resort would change
   *  the rule order, causing a cache miss on the analyst's next manual run. */
  protected _skipNextSortedRules = false;
  /** Set during a page-change-only run (same config, same filters, different page).
   *  Result Summary statistics are unchanged by paging — skip the heavy getSummaryDataFarm call. */
  protected _skipResultSummaryRefresh = false;
  inputFarmGuid?: string;
  minDialogDisplayTimeMs = 1000;
  private formGroups: UntypedFormGroup[] = [];
  parentNodes: Edge[];
  AttrNodes: Edge[];
  refineryConfig: DataRefineryConfig ;
  isParameterFormValid: boolean = false;
  activeSortsSet: SortInfo[]=[];
  activeResultSortsSet: SortInfo[]=[];
  distinctTargetFields: any;
  resultTableColumns:OptionItem[]
  inputcolumncount:number=0;
  outputcolumncount:number=0;
  totalInputRows:number=0;
  inputPrvwFilterViewModel = new InputPrvwFilterViewModel();
  totalSummaryRowsInput:string="";
  totalSummaryRowsOutput:string="";
  totalSummaryRowsLookup:string="";
  totalSummaryRowsA:string="";
  totalSummaryRowsB:string="";
  totalSummaryRowsC:string="";
  totalSummaryRowsD:string="";
  totalSummaryRowsE:string="";
  totalSummaryRowsResult:string="";
  preViewModel:any;
  /** Snapshot of viewModel.transform.analystDescription at the same points preViewModel is
   *  snapshotted (load, and after a successful save) — analystDescription lives outside
   *  transformConfig, so it's tracked separately here rather than folded into preViewModel. */
  preAnalystDescription: string;
  cacheStatus:string="";

  /**
   * Snapshots the current in-editor state as the "last loaded/saved" baseline that
   * isCompiledStale/canDeactivate compare against. Every transformer detail component calls
   * this at the same two points: after loading a config, and after a successful save. Kept
   * as one base-class method (not copy-pasted per subclass) so any future field needing the
   * same dirty-tracking treatment only needs to be added here once.
   */
  protected snapshotPreState(): void {
    this.preViewModel = JSON.parse(JSON.stringify(this.viewModel.toTransformConfig()));
    this.preAnalystDescription = this.viewModel.transform.analystDescription;
  }

  /**
   * Reverts the in-editor view back to the last loaded/saved snapshot (the Architect,
   * 2026-09-27). Called by PendingChangesGuard when the Analyst chooses "leave without
   * saving" -- canDeactivate() only ever compared state to decide whether to WARN, it
   * never actually restored anything, so an unsaved edit (Reset Transformer being the
   * most drastic example, which is what surfaced this) stayed mutated in the shared,
   * persistent dataTransformConfigViewModel object even after the Analyst declined to
   * keep it. Navigating transformer-to-transformer reuses that same shared object, so
   * the stale edit kept reappearing; only a full reload (Node level and back) coincidentally
   * masked it by refetching from the server. Reuses loadImportTransformer() -- the
   * exact same per-type mechanism Reset already uses to go blank -- pointed at
   * preViewModel (already shaped exactly as loadImportTransformer expects, since it's a
   * snapshot of toTransformConfig()'s own output) instead of a blank config. Generic
   * across every transform type; no per-type code needed.
   */
  async revertToSnapshot(): Promise<void> {
    if (!this.preViewModel) return;
    // CRITICAL (the Architect, 2026-09-27): every transform type's own
    // loadImportTransformer() identifies WHICH transform to mutate via
    // this.userData.getSelectedTransform().guid -- not this component's own viewModel.
    // That's harmless for the pre-existing Import/Paste-Config feature (only ever
    // clicked while already viewing that exact transform), but canDeactivate() (and
    // this revert) run as part of navigating AWAY from this transform, and
    // showTransformDetail() may have ALREADY written the DESTINATION transform into
    // that same storage slot moments earlier, as part of the very same click that
    // triggered this navigation. Confirmed: T1 -> T2 with an unsaved T1 edit corrupted
    // T2's data, because loadImportTransformer(T1's preViewModel) mutated whatever guid
    // getSelectedTransform() returned at that moment, which was already T2's.
    //
    // A first attempt forced it to THIS component's transform and left it there --
    // that stopped T2's data from being corrupted, but it then corrupted T2 a
    // different way: T2's own component reads getSelectedTransform() when IT
    // constructs, moments after this guard resolves, and by then it was reading T1's
    // identity instead of the T2 identity showTransformDetail() had already written for
    // it. The pointer has to be borrowed, not just overwritten -- save whatever it
    // currently holds (T2's, already correctly set), point it at THIS transform only
    // for the duration of the revert, then hand it back exactly as found so the
    // destination component still sees its own identity.
    const inFlightNavigationTarget = this.userData.getSelectedTransform();
    this.userData.setSelectedTransform(this.transformViewModel);
    try {
      await this.loadImportTransformer(this.preViewModel);
    } finally {
      this.userData.setSelectedTransform(inFlightNavigationTarget);
    }
    this.viewModel.transform.analystDescription = this.preAnalystDescription;
  }
  private _compiledConfigHash: string | null = null;
  private _compiledAt: Date | null = null;
  private _compiledConfig: any = null;
  // Audit result doesn't need isCompiledStale masking the way compiledConfigHash does --
  // an unsaved edit already shows as "stale vs. last audit" for free once the Audit
  // button compares this against the (now-null) compiledConfigHash above, with no
  // separate tracking needed here.
  lastAuditHash: string | null = null;
  lastAuditResult: string | null = null;

  /**
   * True once the in-editor rules/parameters diverge from the last loaded/saved snapshot
   * (preViewModel) — the same comparison canDeactivate() already uses to detect unsaved
   * changes. Reused generically here so any edit (any rule, any parameter, current or future)
   * invalidates the compiled badge without field-specific tracking.
   */
  get isCompiledStale(): boolean {
    if (!this._compiledConfigHash || !this.preViewModel) return false;
    const current: any = JSON.parse(JSON.stringify(this.viewModel.toTransformConfig()));
    if (!isEqual(this.preViewModel, current)) return true;
    return this.viewModel.transform.analystDescription !== this.preAnalystDescription;
  }

  get compiledConfigHash(): string | null {
    return this.isCompiledStale ? null : this._compiledConfigHash;
  }
  set compiledConfigHash(value: string | null) {
    this._compiledConfigHash = value;
  }

  get compiledAt(): Date | null {
    return this.isCompiledStale ? null : this._compiledAt;
  }
  set compiledAt(value: Date | null) {
    this._compiledAt = value;
  }

  get compiledConfig(): any {
    return this.isCompiledStale ? null : this._compiledConfig;
  }
  set compiledConfig(value: any) {
    this._compiledConfig = value;
  }

  configViewModelObserver = {
    next: (x: DataTransformConfigViewModel) => {
      this.onDataTransformConfigViewModelChanged(x);
    },
    error: (err: Error) => console.error('Observer got an error: ' + err),
    complete: () => console.log('Observer got a complete notification'),
  };

  canDeactivate(): Observable<boolean> | boolean {
    const preViewModel: any = this.preViewModel;
    const viewModel: any = JSON.parse(JSON.stringify(this.viewModel.toTransformConfig()));
    const configUnchanged = isEqual(preViewModel, viewModel);
    // analystDescription lives outside transformConfig, so toTransformConfig() above never
    // sees it — compared separately here or an edited-but-unsaved description would let the
    // analyst navigate away with no warning.
    const descriptionUnchanged = this.viewModel.transform.analystDescription === this.preAnalystDescription;
    return configUnchanged && descriptionUnchanged;
  }
  constructor(protected activeRoute: ActivatedRoute,
    errorService: ErrorReportingService,
    matDialog: MatDialog,
    protected snackBar: MatSnackBar,
    protected router: Router,
    protected configViewModelService: ConfigViewModelService,
    protected configService: ConfigService,
    public navService: NavigationService,
    protected transformPipelineService: TransformPipelineService,
    protected userData: UserDataService,
    protected dataDictService: DataDictionaryService,
    protected refineryPresence: RefineryPresenceService) {
    super(errorService, matDialog);

    const state = history.state;

    this.transformViewModel = this.userData.getSelectedTransform();//state.transformViewModel;
    this.dataTransformConfigViewModel = this.configViewModelService.getDataTransformConfigViewModel();
    const _initDtc = this.dataTransformConfigViewModel?.dataTransformerConfig;
    this.applyCompiledStateFrom(_initDtc);
    this._configVmSub = this.configViewModelService.configViewModel$.subscribe(this.configViewModelObserver);

    this.parentNodes = this.userData.getNodeParentDataLine();
    this.AttrNodes =this.userData.getNodeAttributionLine();

    this.refineryConfig = this.userData.getSelectedRefineryDetails() ;
    // var refineryConfigGuid = this.refineryConfig.guid ;

    if (!_compilableTypesFetchStarted) {
      _compilableTypesFetchStarted = true;
      this.configService.getCompilableTypes()
        .then(resp => { _compilableTransformTypes = new Set(resp.types); })
        .catch(() => { /* best-effort refresh -- keep the fallback guess on failure */ });
    }
  }

  /** Whether this transform type has a real compiled engine -- drives whether
   * a "Run Time (when compiled)" box should render at all on the Results
   * Dashboard. See _compilableTransformTypes' own comment above. */
  get isCompilable(): boolean {
    return _compilableTransformTypes.has(this.viewModel?.transform?.transformType);
  }
  async configureTargetColumnsFarmtypeid(farmtypeid:DataFarmType): Promise<void> {
    var dataDictionaryType=0;
    switch (farmtypeid) {
      case DataFarmType.activity:
        dataDictionaryType=DataDictionaryType.activity;
        break;
      case DataFarmType.subTask:
        dataDictionaryType=DataDictionaryType.subTask;
        break;
      case DataFarmType.task:
        dataDictionaryType=DataDictionaryType.task;
        break;
      case DataFarmType.cases:
        dataDictionaryType=DataDictionaryType.case;
        break;
      case DataFarmType.users:
        dataDictionaryType=DataDictionaryType.user;
        break;
      case DataFarmType.behaviors:
        dataDictionaryType=DataDictionaryType.behavior;
        break;
      case DataFarmType.master:
        dataDictionaryType=DataDictionaryType.master;
        break;  
    }
    this.viewModel.targetColumns =[];
    let dataDictionaryItems: DataDictionaryItem[];
    dataDictionaryItems = this.dataDictService.getColumns(dataDictionaryType);
    this.viewModel.targetColumns = dataDictionaryItems.map(i => i.Field);

    this.viewModel.targetColumns.sort((a, b) => a < b ? -1 : 0);
  }

  ngOnInit(): void {
    // A hard refresh (or a deep link straight into a node) never goes
    // through NodedefinitionComponent.loadConfigDetail() — the only place
    // that normally acquires/renews the Edit lock on open. Without this,
    // RefineryPresenceService.mode is still its just-constructed default
    // ('none') here, so isReadOnly reads true even for the tab that
    // legitimately holds Edit, showing Read-Only after every refresh.
    // mode === 'none' only happens on exactly that cold-load case — normal
    // in-app navigation from the canvas always leaves it 'edit' or
    // 'readOnly' already, so this only fires when there's genuinely
    // nothing to lose by re-attempting: the TabId itself survives a
    // same-tab refresh (sessionStorage), so if this tab already held the
    // lock, the backend recognizes it as the same holder and re-grants it;
    // if not, it correctly falls back to Read-Only, same as any other open.
    if (this.refineryPresence.mode === 'none' && this.refineryConfig?.guid) {
      this.refineryPresence.requestEdit(this.refineryConfig.guid).catch(err =>
        console.error('Failed to re-acquire refinery edit lock on cold load:', err)
      );
    }

    this.activeRoute.params.subscribe(async routeParams => {
      this.syncRouteContext();
      await this.onRouteActivated(routeParams);
    });
  }

  // Template method — each transformer component overrides this with its specific init logic.
  protected async onRouteActivated(_routeParams: any): Promise<void> {}

  // Syncs session with live cockpit state on every route activation.
  protected syncRouteContext(): void {
    if (!this.configViewModelService.getDataTransformConfigViewModel()) {
      // Page refresh: the in-memory service lost its state; reconstruct from storage using
      // fromJSON so all class instances (DataConnectorMapping, DataConnectorViewModel, etc.)
      // are properly typed and their methods are available.
      const stored = this.userData.getSelectedDataTransformConfigViewModel() as any;
      if (stored?.dataTransformerConfig) {
        const config = DataTransformerConfig.fromJSON(stored.dataTransformerConfig);
        this.configViewModelService.setDataTransformConfigViewModel(new DataTransformConfigViewModel(config));
      }
    }
    // When configViewModelService already has a live instance (normal navigation), the
    // navigating component (e.g. transformer-list) set it with proper typed objects just
    // before routing — we keep it as-is rather than overwriting with a localStorage POJO.
    this.transformViewModel = this.userData.getSelectedTransform();
  }

  viewTransformConfigJson(): void {
    this.navService.openSideDrawer();
    const config = this.viewModel.toTransformConfig();
    this.router.navigate(
      [{ outlets: { sidebar: 'transformDetailJsonViewer' } }],
      { relativeTo: this.activeRoute.root, state: { config } }
    );
  }

  async onSortInputFarmChanged(event: any): Promise<void> {
    await this.refreshInputDataFarmPreviewAsync(this.inputDataFarmPreviewTable);
  }

  async onDataFarmSelected(dataFarm: DataFarmViewModel): Promise<void> {
    this.viewModel.onDataFarmSelected(dataFarm);
    await this.downloadInputDataAsync();
  }

  registerFormGroup(group: UntypedFormGroup): void {
    this.formGroups.push(group);
  }

  validate(): boolean {
    let isValid = true;

    const errorMessages = [];

    this.formGroups.forEach(group => {
      group.markAllAsTouched();

      if (isValid) {
        isValid = !group.invalid;
      }

      if (group.invalid) {
        const keys = Object.keys(group.controls);
        keys
          .filter(f => group.get(f).invalid)
          .forEach(key => {
            console.log("key : ",key);
            errorMessages.push(this.viewModel.getValidationInfo(key).errorMessage);
          });
      }
    });
    if(!this.isParameterFormValid){
      isValid = false;//testing later false
    }
    if (!isValid) {
      this.snackBar.openFromComponent(FieldValidationErrorViewComponent, {
        duration: 5000,
        panelClass: 'snackbar-error',
        data: { errorMessages }
      });
    }

    return isValid;
  }

  onDataTransformConfigViewModelChanged(configViewModel: DataTransformConfigViewModel): void {
    this.dataTransformConfigViewModel = configViewModel;
    this.viewModel.refreshDataTransformConfigViewModelAsync(configViewModel);
    // Sync compiled status from this transform itself (populated by GetConfigByGuid / UpsertConfig)
    this.applyCompiledStateFrom(configViewModel?.dataTransformerConfig);
  }

  /**
   * A config/node can hold multiple Row Extractor transforms, each independently compiled —
   * so compiled status must be read off the specific transform being edited here, not the config as a whole.
   * Compiled state lives on the transform itself, not on its transformConfig, so it never enters
   * the transformConfig hash used for caching.
   */
  private applyCompiledStateFrom(dtc: DataTransformerConfig): void {
    const editedGuid = this.transformViewModel?.transform?.guid;
    const matchingTransform: any = dtc?.transforms?.find(t => t.guid === editedGuid);
    this.compiledConfigHash = matchingTransform?.compiledConfigHash ?? null;
    this.compiledAt = matchingTransform?.compiledAt ?? null;
    this.compiledConfig = matchingTransform?.compiledConfig ?? null;
    this.lastAuditHash = matchingTransform?.lastAuditHash ?? null;
    this.lastAuditResult = matchingTransform?.lastAuditResult ?? null;
  }

  getStyleColor(): string {
    switch (this.transformViewModel.transform.dataFarmType) {
      case DataFarmType.activity:
        return '#02b102';
      case DataFarmType.ingest:
        return '#f7df4b';
      case DataFarmType.subTask:
        return '#FF9933';
      case DataFarmType.task:
        return '#3fcdbe';
      case DataFarmType.cases:
        return '#3fcdbe';
      case DataFarmType.users:
        return '#3fcdbe';
      case DataFarmType.behaviors:
        return '#3fcdbe';
      case DataFarmType.master:
        return '#3fcdbe';  
    }
  }
  
  /** Every transform type's own detail component already overrides these three
   *  (required for the pre-existing Import/Paste-Config feature) -- declared here,
   *  with harmless no-op defaults, only so resetTransformer() below can call them
   *  generically without needing each type's own resetTransformer() override. The
   *  no-op default should never actually execute in practice. */
  async loadImportTransformer(transform: any): Promise<void> {}
  async setDefaultValues(transformType: TransformType): Promise<void> {}
  async removeInvalidValues(): Promise<void> {}

  /** Reset Transformer (the Architect, 2026-09-27): generalized from Row Extractor,
   *  which remains the reference implementation. Reverts the in-editor view back to a
   *  blank, unconfigured state -- no rules, all parameters at their defaults. Only
   *  resets the in-editor view, same as Copilot populating a config -- the Analyst
   *  still owns Save. Works for any type whose loadImportTransformer/setDefaultValues/
   *  removeInvalidValues already implement the contract Import/Paste-Config already
   *  depends on -- every type except Farm Joiner and Farm Converter, which are
   *  auto-configured and explicitly excluded (the Architect, 2026-09-27) to avoid
   *  conflicting with that. onAfterResetTransformer() is the one extension point,
   *  empty by default -- Row Extractor overrides it for its own rule-datatype
   *  validation and row-misses side effect, neither of which generalizes. */
  resetTransformer(): void {
    this.showConfirmDialog(
      'Reset transformer?',
      'This clears all rules and parameters back to a blank, unconfigured state. ' +
      'Nothing is saved until you click Save Transformer. Continue?',
      async (confirmed: boolean) => {
        if (!confirmed) return;
        const busyIndicator = this.showBusyDialog('Resetting transformer...');
        try {
          const transformType = this.viewModel.transform.transformType;
          const blankConfig: any = {
            farmType:      this.userData.getSelectedNodeDetails().farmtypeid,
            transformType: transformType,
            rules:         [],
          };
          await this.loadImportTransformer(blankConfig);

          // Default Source Field, Default Target Field, Sample Start/End, Sort By,
          // Include In Results, Run All, etc. are common params owned by the shared
          // TransformerParametersComponent (used identically by every transform type),
          // and are normally only ever defaulted once, the first time a transform's
          // sourceColumns arrive via ngOnChanges. Reset must reproduce that exact same
          // defaulting -- not re-derive its own -- so call it directly on the child
          // component rather than guessing at what a "new transform" would show.
          await this.parametersComponent.setDefaultAndRemoveInvalidValues();

          // loadImportTransformer only copies viewModel.parameters.sampleEnd, never the
          // top-level viewModel.sampleEnd that setDefaultValues() gates on -- force it
          // unconfigured here so the type-specific advanced-setting defaults below
          // actually apply, instead of silently no-op'ing against the pre-reset value.
          this.viewModel.sampleEnd = NaN;
          await this.setDefaultValues(transformType);
          await this.removeInvalidValues();

          // Nothing has run against this blank config -- clear the Results Dashboard
          // and previews rather than leave them showing stale results.
          this.copilotStats        = [];
          this.copilotLog          = [];
          this.copilotContamStats  = [];
          this.lastDtrRunTime      = '';
          this.lastCompiledRunTime = '';
          this.lastDtrThreadsUsed      = null;
          this.lastCompiledThreadsUsed = null;
          this.previewTable?.clear();
          this.outputDataFarmPreviewTable?.clear();
          this.copilotPreviewTable?.clear();

          await this.onAfterResetTransformer();

          this._copilotCtx.emitResetTransformer();
        } finally {
          busyIndicator.close();
        }
      }
    );
  }

  /** Extension point for a type-specific post-reset step that doesn't generalize --
   *  e.g. Row Extractor's own rule-datatype validation and row-misses side effect.
   *  Empty by default; override in a subclass only when genuinely needed. */
  protected async onAfterResetTransformer(): Promise<void> {}

  showTransformDetail(transformId: string): void {
    // A disabled transformer's own detail page can't be opened at all (the Architect,
    // 2026-09-27) -- matches the Node page's own showTransformDetail guard
    // (transformer-list.component.ts). This is the SECOND navigation surface that reaches
    // a transformer's detail page (the T1 --> T2 --> ... breadcrumb at the top of every
    // transform detail page, transformer-top-header.component.html), which bypassed that
    // guard entirely since it never routes through the Node page's own click handler.
    const targetTransform = this.dataTransformConfigViewModel.transforms.find(t => t.guid === transformId);
    if (targetTransform?.transform.run === false) {
      this.displayWarning(
        'Disabled Transformers cannot be edited. Please enable this transformer if you would like to edit/open it.',
        'Transformer disabled at Node level'
      );
      return;
    }

    let configId = this.dataTransformConfigViewModel.dataTransformerConfig?.guid;
    if (!configId) {
      configId = null;
    }
    var transform= targetTransform;
    var newurl="";
    switch (transform.type) {
      case TransformType.rowExtractor:
        newurl=`dataTransformConfig/${configId}/extractorTransformDetail/${transform.guid}`;
        break;

      case TransformType.activityNamer:
        newurl=`dataTransformConfig/${configId}/activityNamingTransformDetail/${transform.guid}`;
        break;

      case TransformType.eventGroupNamer:
        newurl=`dataTransformConfig/${configId}/eventGroupNamerTransformDetail/${transform.guid}`;
        break;

      case TransformType.eventNamer:
        newurl=`dataTransformConfig/${configId}/eventNamerTransformDetail/${transform.guid}`;
        break;

      case TransformType.groupAttributor:
        newurl=`dataTransformConfig/${configId}/groupAttributorTransformDetail/${transform.guid}`;
        break;

      case TransformType.columnCalc:
        newurl=`dataTransformConfig/${configId}/columnCalcTransformDetail/${transform.guid}`;
        break;
      case TransformType.columnLookup:
        newurl=`dataTransformConfig/${configId}/columnLookupTransformDetail/${transform.guid}`;
        break;
      case TransformType.rowFiller:
        newurl=`dataTransformConfig/${configId}/rowFillerTransformDetail/${transform.guid}`;
        break;
      case TransformType.anonymizer:
        newurl=`dataTransformConfig/${configId}/anonymizerTransformDetail/${transform.guid}`;
        break;
      case TransformType.columnCleaner:
        newurl=`dataTransformConfig/${configId}/columnCleanerTransformDetail/${transform.guid}`;
        break;
    }

    if(transform.type==this.viewModel.transform.transformType){
      // Same-type navigation (includes clicking the CURRENTLY-open transformer's own
      // breadcrumb link) forces a hard reload via this /refresh hop, since Angular
      // wouldn't otherwise recreate the component. THIS is where canDeactivate actually
      // runs on the transformer being navigated away from -- which, for self-navigation,
      // is this exact transformId. Writing "selected transform" to storage (a JSON
      // snapshot, not a live reference -- see StorageService.set) must wait until AFTER
      // this resolves, or it permanently captures whatever was on screen BEFORE the
      // guard's revertToSnapshot() ran (the Architect, 2026-09-27) -- e.g. a just-Reset,
      // never-saved config -- and the newly-constructed component reads that stale
      // snapshot back as if it were current. navigated===false means the Analyst chose
      // Cancel in the unsaved-changes dialog -- stay put, don't force navigation anyway.
      this.router.navigateByUrl(`/refresh`, { skipLocationChange: true })
      .then((navigated) => {
        if (!navigated) return;
        const freshTransform = this.dataTransformConfigViewModel.transforms.find(t => t.guid === transformId);
        this.dataTransformConfigViewModel.activeTransformGuid = transformId;
        this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
        this.userData.setSelectedTransform(freshTransform);
        this.userData.setTransformId(freshTransform.guid);
        this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);
        this.router.navigate([newurl], {state: {transformViewModel: freshTransform}});
      });
    }else{
      // Different-type navigation: canDeactivate's revert (if any) affects the SOURCE
      // transform, not this DIFFERENT-type destination -- writing its data before
      // navigating is unaffected by that timing and is what makes the new component's
      // own construction see it immediately.
      this.dataTransformConfigViewModel.activeTransformGuid = transformId;
      this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
      this.userData.setSelectedTransform(transform);
      this.userData.setTransformId(transform.guid);
      this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);
      this.router.navigateByUrl(newurl,
          {state: {transformViewModel: transform}});
    }
  }

  getClassColor(dark: boolean = false): string {
    switch (this.transformViewModel.transform.dataFarmType) {
      case DataFarmType.activity:
        return dark ? 'activity-color-dark' : 'activity-color';
      case DataFarmType.ingest:
        return dark ? 'ingest-color-dark' : 'ingest-color';
      case DataFarmType.subTask:
        return dark ? 'subTask-color-dark' : 'subTask-color';
      case DataFarmType.task:
        return dark ? 'task-color-dark' : 'task-color';
      case DataFarmType.cases:
        return dark ? 'task-color-dark' : 'task-color';
      case DataFarmType.users:
        return dark ? 'task-color-dark' : 'task-color';
      case DataFarmType.behaviors:
        return dark ? 'task-color-dark' : 'task-color';
      case DataFarmType.master:
        return dark ? 'task-color-dark' : 'task-color';  
    }
  }

  async ngAfterContentInit(): Promise<void> {
    this.formGroups.forEach(f => f.markAllAsTouched());
  }

  replacer = (key, value) =>
    value instanceof Object && !(value instanceof Array) ?
      Object.keys(value)
        .sort()
        .reduce((s, k) => {
          s[k] = value[k];
          return s;
        }, {}) : value

  protected async downloadInputDataAsync(): Promise<void> {
    const pipelineVm = new TransformPipelineViewModel();
    const busyIndicator = this.showPipelineDialog(pipelineVm, 'Loading pipeline data...');//'Checking transforms...'
    const busyIndicatorComp = busyIndicator?.componentInstance;
    try {
      var nodeID=this.userData.getSelectedNodeDetails().id;
      var refineryID=this.userData.getSelectedNodeDetails().refineryConfigGuid;
      const pipeline = this.transformPipelineService.buildPipeline(
        this.transformViewModel.guid,
        this.dataTransformConfigViewModel.transforms.filter(x=>x.nodeID === nodeID));

      if (pipeline.transforms.length > 0) {
        // remove this transform when getting input data
        for (const group of pipeline.groupedTransforms) {
          for (let index = 0; index < group.transforms.length; index++) {
            if (group.transforms[index].guid === this.transformViewModel.guid) {
              group.transforms.splice(index, 1);
            }
          }
        }
      }
      // Build view Model of Transform Pipeline
      pipelineVm.items = pipeline.groupedTransforms.map(g => {
        const group = new GroupedTransformPipelineItem();
        group.groupName = g.groupName;
        group.items = g.transforms.map(t => {
          return new TransformPipelineItem(t.name, g.groupName, 'Pending', t, t.guid === this.transformViewModel.guid,"","");
        });
        return group;
      }
      );
      if(pipeline.transforms.length==1){
        const groupedItem = new GroupedTransformPipelineItem();
        groupedItem.groupName="";
        groupedItem.items.push(new TransformPipelineItem("Load Input Farm", "", 'Pending', null, true,"",""));
        pipelineVm.items.push(groupedItem);
      }
      const dataConnectors = this.viewModel.buildDataConnectorMappings();
      const activeDataConnector = dataConnectors.find(d => d.guid === this.dataTransformConfigViewModel.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.dataTransformConfigViewModel.selectedIngestFarmGuid}`);
      }
      const activeTransformGuid = this.userData.getSelectedTransform().guid
      const transformerRunType = "transformer_prev"
      // Execute the pipeline
      await this.transformPipelineService.executePipelineAsync(
        refineryID,
        this.parentNodes, 
        this.AttrNodes, 
        pipeline,
        activeDataConnector.guid,
        transformerRunType,
        activeTransformGuid,
        async (transform, groupName) => {
          pipelineVm.currentGroup = groupName;
          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transform)[0];

          // Update the active source data farm, even though it's not the first one
          // this should make the transform dirty so it runs again
          // as the root input farm has changed.
          // TODO: Perhaps we us an isDirty or invalidated property instead?
          item.isRunning = true;
          item.statusDisplayText = 'Executing...';
          this.inputFarmGuid = transform.guid;
        },
        (request) => {
          if (this.compiledConfig) { request.compiledConfig = this.compiledConfig; }
        },
        (transformerViewModel, groupName, success, response, err) => {
          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transformerViewModel)[0];
          item.isRunning = false;
          item.statusDisplayText = success ? 'Completed!' : `Failed!`;
          // transform-pipeline.service.ts passes response=null whenever the backend call
          // failed (onTransformCompleted(..., false, null, err)) -- reading response.* here
          // unconditionally crashed with a null-reference TypeError on every real pipeline
          // failure, BEFORE the err check below ever ran, which meant the real backend error
          // was never shown to the Architect. Found live, 2026-10-03.
          if (response) {
            item.cacheUpdatedStatus = response.cacheUpdatedStatus;
            item.transformerTime = response.transformerTime;
          }

          if (err) {
            this.displayError('Failed to get input data', 'Error', err);
          }
        }, (transformerViewModel) => {
          // Ensure we set Transient runAll property to true for all pre transforms so full set of data is processed
          transformerViewModel.transform.setRunAll(true);
          return null;
      });
      await this.refreshInputDataFarmPreviewAsync(this.inputDataFarmPreviewTable);
      if(pipeline.transforms.length==1){
        const item = pipelineVm.items.filter(g => g.groupName === "")[0].items[0];
        item.isRunning = false;
        item.statusDisplayText = 'Completed!';
        item.cacheUpdatedStatus ="Cached";
        item.transformerTime =this.inputDataFarmTime;
      }
      this.inputPrvwFilterViewModel.farmId = (this.inputFarmGuid == undefined) ? "" : this.inputFarmGuid;
      this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey = (this.inputFarmGuid == undefined) ? "" : this.userData.getSelectedNodeDetails().id + "-" + this.inputFarmGuid;
      this.totalInputRows =  this.inputDataFarmPreviewTable.totalRows;
      await this.getCacheStatus();
      this.updateNodeStatus();
      // Input cache key already refreshed inside refreshInputDataFarmPreviewAsync() above.
      //Load Summary Preferences
      // if(this.viewModel.transform.transformType==TransformType.farmJoiner){
        //this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableOutput);
        // this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableA);
        // this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableB);
        // this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableC);
        // this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableD);
        // this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableE);
      // }else 
      if(this.viewModel.transform.transformType==TransformType.columnLookup){
        this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableInput);
        //this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableOutput);
        this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableLookup);
      }else{
        this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableInput);
        //this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableOutput);
      }
    } catch (ex) {
      this.displayError('Failed to get input data', 'Error', ex);
    } finally {
      setTimeout(() => {
        busyIndicatorComp.close();
      }, 1000); // 1 seconds delay
    }
  }
  protected async downloadInputDataAsyncOld(): Promise<void> {
    //const busyIndicator = this.showBusyDialog('Loading pipeline data.....');
    const minDisplayEndTime = new Date(Date.now() + this.minDialogDisplayTimeMs);
    const pipelineVm = new TransformPipelineViewModel();
    const busyIndicator = this.showPipelineDialog(pipelineVm, 'Loading pipeline data...');//'Checking transforms...'
    const busyIndicatorComp = busyIndicator?.componentInstance;
    try {
      // Track last sortInfo used, if it changes we will update the input farm 
      // on next run of the transform
      const sortInfoJson = JSON.stringify(this.viewModel.sortInfo);
      this.viewModel.lastSortInfoJson = sortInfoJson;
      // var CurrentNode = this.userData.getSelectedNodeDetails();

      // Add NodeID to this.dataTransformConfigViewModel.transforms

      // this.dataTransformConfigViewModel.transforms.forEach(subArray => {
      //   // Add NodeID property to each sub-transform
      //   subArray.transform.nodeID = CurrentNode.id;
      // });
      var nodeID=this.userData.getSelectedNodeDetails().id;
      var refineryID=this.userData.getSelectedNodeDetails().refineryConfigGuid;
      const pipeline = this.transformPipelineService.buildPipeline(
        this.transformViewModel.guid,
        this.dataTransformConfigViewModel.transforms.filter(x=>x.nodeID === nodeID));

      if (pipeline.transforms.length > 0) {
        // remove this transform when getting input data
        for (const group of pipeline.groupedTransforms) {
          for (let index = 0; index < group.transforms.length; index++) {
            if (group.transforms[index].guid === this.transformViewModel.guid) {
              group.transforms.splice(index, 1);
            }
          }
        }
      }
      // Build view Model of Transform Pipeline
      pipelineVm.items = pipeline.groupedTransforms.map(g => {
        const group = new GroupedTransformPipelineItem();
        group.groupName = g.groupName;
        group.items = g.transforms.map(t => {
          return new TransformPipelineItem(t.name, g.groupName, 'Pending', t, t.guid === this.transformViewModel.guid,"","");
        });
        return group;
      }
      );
      if(pipeline.transforms.length==1){
        const groupedItem = new GroupedTransformPipelineItem();
        groupedItem.groupName="";
        groupedItem.items.push(new TransformPipelineItem("Loading Input Node Data...", "", '', null, true,"",""));
        pipelineVm.items.push(groupedItem);
      }
     
      const dataConnectors = this.viewModel.buildDataConnectorMappings();

      const activeDataConnector = dataConnectors.find(d => d.guid === this.dataTransformConfigViewModel.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.dataTransformConfigViewModel.selectedIngestFarmGuid}`);
      }
      const activeTransformGuid = this.userData.getSelectedTransform().guid
      const transformerRunType = "transformer_prev"
      
      // Execute the pipeline
      await this.transformPipelineService.executePipelineAsyncOld(
        refineryID,
        this.parentNodes, 
        this.AttrNodes, 
        pipeline,
        dataConnectors,
        activeDataConnector.guid,
        transformerRunType,
        activeTransformGuid,
        async (transform, groupName) => {
          pipelineVm.currentGroup = groupName;
          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transform)[0];

          // Update the active source data farm, even though it's not the first one
          // this should make the transform dirty so it runs again
          // as the root input farm has changed.
          // TODO: Perhaps we us an isDirty or invalidated property instead?
          item.isRunning = true;
          item.statusDisplayText = 'Executing...';
          this.inputFarmGuid = transform.guid;
        },
        (request) => {
          if (this.compiledConfig) { request.compiledConfig = this.compiledConfig; }
        },
        (transformerViewModel, groupName, success, response, err) => {
          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transformerViewModel)[0];
          item.isRunning = false;
          item.statusDisplayText = success ? 'Completed!' : `Failed!`;
          // transform-pipeline.service.ts passes response=null whenever the backend call
          // failed (onTransformCompleted(..., false, null, err)) -- reading response.* here
          // unconditionally crashed with a null-reference TypeError on every real pipeline
          // failure, BEFORE the err check below ever ran, which meant the real backend error
          // was never shown to the Architect. Found live, 2026-10-03.
          if (response) {
            item.cacheUpdatedStatus = response.cacheUpdatedStatus;
            item.transformerTime = response.transformerTime;
          }

          if (err) {
            this.displayError('Failed to get input data', 'Error', err);
          }
        }, (transformerViewModel) => {
          // Ensure we set Transient runAll property to true for all pre transforms so full set of data is processed
          transformerViewModel.transform.setRunAll(true);
          return null;
        });

      await this.refreshInputDataFarmPreviewAsync(this.inputDataFarmPreviewTable);
      this.inputPrvwFilterViewModel.farmId = (this.inputFarmGuid == undefined) ? "" : this.inputFarmGuid;
      this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey = (this.inputFarmGuid == undefined) ? "" : this.userData.getSelectedNodeDetails().id + "-" + this.inputFarmGuid;
      this.totalInputRows =  this.inputDataFarmPreviewTable.totalRows;
      // Input cache key already refreshed inside refreshInputDataFarmPreviewAsync() above.
    } catch (ex) {
      this.displayError('Failed to get input data', 'Error', ex);
    } finally {
      setTimeout(() => {
        busyIndicatorComp.close();
      }, 1000); // 1 seconds delay
    }
  }

  async runTransform(
    filters?: ColumnFilterInfo[],
    outputFarmFilters?: ColumnFilterInfo[],
    showDialog: boolean = true,
    showCompletionMessage: boolean = true): Promise<void> {
    if (!this.validate()) {
      return;
    }
    if(!this.userData.getApplyFilterBtnClicked()) {
        this.userData.setToClearActiveFilters(true);
    }
    this.viewModel.isTransformRunning = true;
    const minDisplayEndTime = new Date(Date.now() + this.minDialogDisplayTimeMs);
    const pipelineVm = new TransformPipelineViewModel();
    const busyIndicator = showDialog ? this.showPipelineDialog(pipelineVm, 'Executing transforms...') : null;
    const busyIndicatorComp = busyIndicator?.componentInstance;  // hold on to reference or else will be null after it is closed

    try {
      // Keep track of if sortInfo changed
      const sortInfoJson = JSON.stringify(this.viewModel.sortInfo);
      const didSortChange = sortInfoJson !== this.viewModel.lastSortInfoJson;
      this.viewModel.lastSortInfoJson = sortInfoJson;
      var CurrentNode = this.userData.getSelectedNodeDetails();
      // Refresh input farm async to keep sort order in sync with output
      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
        if (didSortChange) {
          this.refreshInputDataFarmPreviewAsync(this.inputDataFarmPreviewTable);
        }
      }
      var currenttransformer = this.viewModel.toTransformConfig();
      var currenttransformer_sampleStart = currenttransformer.sampleStart + 1 ;
      var currenttransformer_sampleEnd = currenttransformer.sampleEnd + 1 ;
      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
     if (currenttransformer_sampleStart >= currenttransformer_sampleEnd) {
            this.displayWarning(
              'Sample Start was greater than to Sample End, so it was auto-reset to 1.',
              'Warning',
              undefined,
              () => {
                this.viewModel.parameters.sampleStart = 1;
              }
            );
            return;
          }
        if(currenttransformer_sampleStart > this.totalInputRows || currenttransformer_sampleEnd > this.totalInputRows )
        {
          // this.viewModel.parameters.sampleStart = 1;
          // this.viewModel.parameters.sampleEnd = this.inputDataFarmPreviewTable.totalRows - 1;
          this.displayWarning(
            'Sample Range exceeded the Input Farm size and was auto-corrected to the maximum limit.',
            'Warning',
            undefined,  // no exception to pass
            () => {
              //this.viewModel.parameters.sampleEnd = this.totalInputRows;
              if (currenttransformer_sampleStart > this.totalInputRows) {
              this.viewModel.parameters.sampleStart = 1;
              }
              if (currenttransformer_sampleEnd > this.totalInputRows) {
                this.viewModel.parameters.sampleEnd = this.totalInputRows;
              }
            }
          );
          return;
        }
      }
      // We need to build the transform pipeline
      // and execute each transform starting from the top
      var nodeID=this.userData.getSelectedNodeDetails().id;
      var refineryID=this.userData.getSelectedNodeDetails().refineryConfigGuid  ;
      const pipeline = this.transformPipelineService.buildPipeline(
        this.transformViewModel.guid,
        this.dataTransformConfigViewModel.transforms.filter(x=>x.nodeID === nodeID));
      // Build view Model of Transform Pipeline
      pipelineVm.items = pipeline.groupedTransforms.map(g => {
        const group = new GroupedTransformPipelineItem();
        group.groupName = g.groupName;
        group.items = g.transforms.map(t => {
          return new TransformPipelineItem(t.name, g.groupName, 'Pending', t, t.guid === this.transformViewModel.guid,"","");
        });
        return group;
      }
      );

      const dataConnectors = this.viewModel.buildDataConnectorMappings();
      // Get the active input ingest data farm (data connector)
      // and ensure every transform runs with the correct one

      const activeDataConnector = dataConnectors.find(d => d.guid === this.dataTransformConfigViewModel.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.dataTransformConfigViewModel.selectedIngestFarmGuid}`);
      }

      let didFail = false;
      let transformerRunType = (this.userData.getSelectedNodeDetails().nodeboxtypename == "Joiner" || this.userData.getSelectedNodeDetails().nodeboxtypename == "Converter") ? "transformer_prev" : "transformer_run"
      const activeTransformGuid = this.userData.getSelectedTransform().guid
      // Execute the pipeline
      await this.transformPipelineService.executePipelineAsyncOld(
        refineryID,
        this.parentNodes, 
        this.AttrNodes, 
        pipeline,
        dataConnectors,
        activeDataConnector.guid,
        transformerRunType,
        activeTransformGuid,
         (transform, groupName) => {
          pipelineVm.currentGroup = groupName;

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transform)[0];

          // Update the active source data farm, even though it's not the first one
          // this should make the transform dirty so it runs again
          // as the root input farm has changed.
          // TODO: Perhaps we us an isDirty or invalidated property instead?
          item.isRunning = true;
          item.statusDisplayText = 'Executing...';
        },
        (request) => { if (this.compiledConfig) { request.compiledConfig = this.compiledConfig; } },
        (transformerViewModel, groupName, success, response, err) => {
          if(!success){
            this.logError("Failed to run transform",err);
          }
          didFail = !success && err != null;

          if (busyIndicatorComp) {
            busyIndicatorComp.hasError = didFail;
          }

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transformerViewModel)[0];
          item.isRunning = false;
          item.statusDisplayText = success ? 'Completed!' : `Failed!`;
          // response=null on failure (see the other fixed call sites in this file for the
          // full explanation) -- guarded the same way here.
          if (response) {
            item.cacheUpdatedStatus = response.cacheUpdatedStatus;
            item.transformerTime = response.transformerTime;
          }

          if (transformerViewModel.guid === this.transformViewModel.guid && response != null) {
            this.previewTable.bindData(response.activeData, 'No Results');
            this.outputDataFarmPreviewTable.bindData(response.currentData, 'No Farm Data');
            this.outputcolumncount=response.currentData.columns.length;

            if (showCompletionMessage) {
              this.snackBar.open(`Transform results have been updated!`, 'Dismiss', {
                duration: 3000
              });
            }

            if(this.viewModel.transform.transformType==TransformType.farmConverter){
              this.viewModel.onTransformRunCompleted(response as FarmConverterTransformPreviewDataResponse);
            }else{
              this.viewModel.onTransformRunCompleted(response as RowExtractorPreviewDataResponse);
            }
          }
        }, transformerViewModel => {

          // Build the current transform request so we can render it's results properly
          if (transformerViewModel.guid === this.transformViewModel.guid) {
            const previewOptions = new PreviewDataOptions(this.previewTable.pageIndex, this.previewTable.rowsPerPage,this.activeResultSortsSet);
            previewOptions.filters = filters?.map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });

              this.activeSortsSet = this.activeSortsSet.filter(
               item => !item.sortField.startsWith("smry_count")
            );
            
            const farmDataPreviewOptions = new PreviewDataOptions(
              this.outputDataFarmPreviewTable.pageIndex,
              this.outputDataFarmPreviewTable.rowsPerPage,this.activeSortsSet);
            farmDataPreviewOptions.filters = outputFarmFilters?.map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });

            const request = new RunConfigRequest();
            request.activeIngestFarmId = activeDataConnector.guid;
            request.requireResults = true;
            request.previewOptions = previewOptions;
            request.farmDataOutPreviewOptions = farmDataPreviewOptions;
            request.dataSourceType = DataSourceType.timeStampedData;
            request.activeTransformId = activeTransformGuid;
            request.transformerRunType = transformerRunType;
            request.userId = this.userData.getUserId();
            request.orgId = this.userData.getOrgId();
            request.refineryID = this.userData.getRefineryId();
            request.nodeId = this.userData.getNodeId();
            // Only send up the data connectors if we are the first transform
            // as this one reads from the data connector for input
            if (pipeline.transforms.indexOf(transformerViewModel) === 0) {
              request.dataConnectorMappings = dataConnectors;
            }

            request.transforms = [];
            request.transforms.push(transformerViewModel.transform);

            const transformConfig = this.viewModel.toTransformConfig();
            transformerViewModel.transform.transformConfig = transformConfig;
            if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
              if(transformerViewModel.transform.transformConfig.rules){
                var rulesData = transformerViewModel.transform.transformConfig.rules;
                this.resultTableColumns = [...this.viewModel.sourceColumns] ;
                // Define the new column object with the next index for Input string to make yellow and white - DT-1288
                const newColumn = {
                  "value": 'Input string',
                  "text": 'Input string',
                  "isSelected": false
                };
                this.resultTableColumns.push(newColumn);

                const distinctTargetFields = (data: any[]): string[] => data.map(item => item.targetField).filter((value, index, self) => self.indexOf(value) === index);
                
                const distinctFields = distinctTargetFields(rulesData);

                distinctFields.forEach(field => {
                    const newColumn = {
                        "value": field,
                        "text": field,
                        "isSelected": false
                    };
                    this.resultTableColumns.push(newColumn);
                });   
              
              }
            }
            
            return request;
          } else {
            // Ensure we set Transient runAll property to true for all pre transforms so full set of data is processed 
            transformerViewModel.transform.setRunAll(true);

            return null;
          }
      });

        if(this.outputDataFarmPreviewTable.totalRows < 1 && this.viewModel.toTransformConfig().runAll == true){
          this.displayWarning('This configuration produces an empty (0 rows) output data farm.  This will causes failures in downstream processing.  Please correct, test and save.', 'Warning');
        }

        //Summary Refresh
        // if(this.outputDataFarmPreviewTable.displayedColumnInfos){
        //   this.refreshSummaryPreviewTable();
        // }
    } catch (err) {
      this.displayError('Failed to run transform', 'Error', err);
    } finally {
      const diff = Date.now() - minDisplayEndTime.getTime();
      if (showDialog && diff < 0) {
        await this.delay(Math.abs((diff)));
      }

      if (busyIndicatorComp) {
        if (busyIndicatorComp.hasError) {
          await this.delay(4000);
        }

        busyIndicatorComp.close();
      }

      this.viewModel.isTransformRunning = false;
      this.userData.setApplyFilterBtnClicked(false);
    }
  }

  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  showPipelineDialog(viewModel: TransformPipelineViewModel, message?: string): MatDialogRef<TransformPipelineDialogComponent> {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = 'border-dialog';

    dialogConfig.data = {
      message,
      viewModel
    };

    return this.matDialog.open(TransformPipelineDialogComponent, dialogConfig);
  }

  showSaveConfigDialog(onClosed: (name: string) => any): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = { configName: this.dataTransformConfigViewModel.name };

    const dialogRef = this.matDialog.open(SaveMappingConfigDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.name) { return; }

      // Name entered
      onClosed(result.name);
    });
  }
  inputDataFarmTime:string="";
  async refreshInputDataFarmPreviewAsync(sender: PreviewTableComponent): Promise<void> {
    this.inputDataFarmTime="";
    this.inputFarmGuid
    
    sender.isGettingData.next(true);

    try {
      const dataConnectors = this.viewModel.buildDataConnectorMappings();
      const activeDataConnector = dataConnectors.find(d => d.guid === this.dataTransformConfigViewModel.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.dataTransformConfigViewModel.selectedIngestFarmGuid}`);
      }

      const colMap = this.viewModel.dataFarm.columnMappings.map(c => {
        return new ColumnMapDefinition(c.id, c.columnName, c.targetColumnName, c.dataType, c.pyDataType);
      });
      const dataSourceType = this.dataTransformConfigViewModel.dataSourceType;
      const dataConnectorMapping = new DataConnectorMapping(
        this.viewModel.dataFarm.id,
        this.viewModel.dataFarm.name,
        this.viewModel.dataFarm.dataFarmType,
        this.viewModel.dataFarm.dataConnector.toDataConnector(),
        colMap,
        this.viewModel.dataFarm.guid
      );
      const filters = sender.activeFilterSet?.map(f => {
        const filterInfo = new ColumnFilter();
        filterInfo.field = f.columnName;
        filterInfo.values = f.values;
        return filterInfo;
      });

      const sortInfo = this.activeSortsSet;//this.viewModel.sortInputFarm ? this.viewModel.sortInfo : null;
      const options = new PreviewDataOptions(sender.pageIndex, sender.rowsPerPage, sortInfo, filters);
      const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transInput);
      // Smart Sample's input cache key — every path that refreshes the input
      // farm preview needs this current, not just the two call sites that used
      // to compute it redundantly right after calling this method. Independent
      // of Copilot AI-chat registration (_copilotCtx.update() is still called
      // too, for types that do have a registered agent).
      {
        const copilotCacheKey = cachekeyRequest.cacheKeyType === CacheKeyType.node
          ? this.userData.getRefineryId() + '_' + cachekeyRequest.nodeId
          : this.userData.getRefineryId() + '_' + this.userData.getNodeId() + '_' + cachekeyRequest.transformId;
        this._smartSampleInputCacheKey = copilotCacheKey;
        this._copilotCtx.update({ inputCacheKey: copilotCacheKey });
      }
      const request = new GetDataFarmPreviewRequest(
        cachekeyRequest.userId,
        cachekeyRequest.orgId,
        cachekeyRequest.refineryId,
        cachekeyRequest.nodeId,
        cachekeyRequest.transformId,
        cachekeyRequest.cacheKeyType,
        options);
      const response = await this.configService.getDataFarmPreviewAsync(request);
      
      const columns = response.data.columns;
      
      // Sort by column index for display
      columns.sort((a, b) => a.index - b.index);
      // build the source columns not that we have them
      // make copy of the columns so we can sort differently and not affect original list
      //if (sender === this.inputDataFarmPreviewTable){
      this.viewModel.updateInputFarmColumns([...columns]);
      //}

      sender.bindData(response.data, 'No input preview data');
      if (sender == this.inputDataFarmPreviewTable) {
        this.inputcolumncount=response.data.columns.length;
      } else if (sender == this.outputDataFarmPreviewTable){
        this.outputcolumncount=response.data.columns.length;
      }
      this.inputDataFarmTime=response.timeTaken;
      this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableInput, true);
      
    } catch (err) { 
      this.displayError('Failed to get data farm preview ', 'Error', err);
    } finally {
      sender.isGettingData.next(false);
    }
  }
  /** Fetch a page of raw result rows from the server's transResultTemp cache.
   *  Same lightweight path as Data Preview — no transform re-run. */
  async refreshResultsPreviewAsync(sender: PreviewTableComponent): Promise<void> {
    sender.isGettingData.next(true);
    try {
      const filters = (sender.activeFilterSet ?? []).map(f => {
        const filterInfo = new ColumnFilter();
        filterInfo.field = f.columnName;
        filterInfo.values = f.values;
        return filterInfo;
      });
      const options = new PreviewDataOptions(sender.pageIndex, sender.rowsPerPage, this.activeResultSortsSet, filters);
      const cachekeyRequest = await this.GetCacheKeyRequest(PreviewType.transResultTemp);
      const request = new GetDataFarmPreviewRequest(
        cachekeyRequest.userId,
        cachekeyRequest.orgId,
        cachekeyRequest.refineryId,
        cachekeyRequest.nodeId,
        cachekeyRequest.transformId,
        cachekeyRequest.cacheKeyType,
        options
      );
      const response = await this.configService.getDataFarmPreviewAsync(request);
      sender.bindData(response.data, 'No Results');
    } catch (err) {
      this.displayError('Failed to load results preview', 'Error', err);
    } finally {
      sender.isGettingData.next(false);
    }
  }

  stringToBoolean(str: string): boolean {
    return str.toLowerCase() === 'true';
  }
  colorSummaryCount:string="";
  getClassForRowBase(sender: PreviewTableComponent, row: any, colInfo: ColumnInfo, colInfos: ColumnInfo[]): string {
    try {
      if (sender === this.previewTable) {
        if (this.viewModel.sourceColumns && this.viewModel.sourceColumns.find(f => f.text === colInfo.name) === undefined) {
          return 'meta-data-column-color';
        }
        
        // const missCol = colInfos.filter( col => {
        //   return col.name === 'Rule';
        // });

        // if (missCol.length === 1) {
        //   if (row[missCol[0].index] === 0) {
        //     return 'missedRuleRow';
        //   }
        // }
        const missCol = colInfos.find(col => col.name === 'Rule');
        if (missCol && row && missCol.index !== undefined) {
          if (row[missCol.index] === 0) {
            return 'missedRuleRow';
          }
        }

      }else if (sender === this.summaryDataFarmPreviewTableResult) {
        if(colInfo.name.startsWith("smry_count")){
          if(this.colorSummaryCount!=""){
            this.colorSummaryCount="";
            return 'meta-data-column-color';
          }
        }else{
          if (this.viewModel.sourceColumns && this.viewModel.sourceColumns.find(f => f.text === colInfo.name) === undefined) {
            this.colorSummaryCount='meta-data-column-color';
            return 'meta-data-column-color';
          }
        }
        
        // const missCol = colInfos.filter( col => {
        //   return col.name === 'Rule';
        // });

        // if (missCol.length === 1) {
        //   if (row[missCol[0].index] === 0) {
        //     return 'missedRuleRow';
        //   }
        // }
        const missCol = colInfos.find(col => col.name === 'Rule');

        if (missCol && row && missCol.index !== undefined) {
          if (row[missCol.index] === 0) {
            return 'missedRuleRow';
          }
        }
      } else if (sender === this.copilotPreviewTable) {
        return colInfo.index === 0 ? null : 'meta-data-column-color';
      } else if (sender === this.auditPreviewTable) {
        if (!row) return null;
        const isMiss   = row['__isMiss'];
        const isContam = row['__isContaminated'];
        const colName  = colInfo.name;
        if (colName === 'Row') return null;
        if (isMiss)   return 'missedRuleRow';
        if (isContam) return 'audit-contam-cell';
        if (colName.endsWith(' Input Data')) return null;
        return 'meta-data-column-color';
      }
    } catch (err) {
      console.log(err);
    }

    return null;
  }

  //code for summary preview
  source: string="";
  datafarmSources?: any[]=[];

  sourceResult: string="";
  datafarmSourcesResult?: any[]=[];

  selectValues?: any[]=[];
  selectValuesInput?: any[]=[];
  selectValuesOutput?: any[]=[];
  selectValuesLookup?: any[]=[];
  selectValuesA?: any[]=[];
  selectValuesB?: any[]=[];
  selectValuesC?: any[]=[];
  selectValuesD?: any[]=[];
  selectValuesE?: any[]=[];
  selectValuesResult?: any[]=[];
  selectValuesColorResult?: ValuesColor[]=[];

  selectEmptyColumns?: any[]=[];
  selectEmptyColumnsInput?: any[]=[];
  selectEmptyColumnsOutput?: any[]=[];
  selectEmptyColumnsLookup?: any[]=[];
  selectEmptyColumnsA?: any[]=[];
  selectEmptyColumnsB?: any[]=[];
  selectEmptyColumnsC?: any[]=[];
  selectEmptyColumnsD?: any[]=[];
  selectEmptyColumnsE?: any[]=[];
  selectEmptyColumnsResult?: any[]=[];

  selectFilterValues?: any[]=[];
  selectFilterValuesInput?: any[]=[];
  selectFilterValuesOutput?: any[]=[];
  selectFilterValuesLookup?: any[]=[];
  selectFilterValuesA?: any[]=[];
  selectFilterValuesB?: any[]=[];
  selectFilterValuesC?: any[]=[];
  selectFilterValuesD?: any[]=[];
  selectFilterValuesE?: any[]=[];
  selectFilterValuesResult?: any[]=[];

  summaryFilterInput?: ColumnFilterInfo[]=[];
  summaryFilterOutput?: ColumnFilterInfo[]=[];
  summaryFilterLookup?: ColumnFilterInfo[]=[];
  summaryFilterA?: ColumnFilterInfo[]=[];
  summaryFilterB?: ColumnFilterInfo[]=[];
  summaryFilterC?: ColumnFilterInfo[]=[];
  summaryFilterD?: ColumnFilterInfo[]=[];
  summaryFilterE?: ColumnFilterInfo[]=[];
  summaryFilterResult?: ColumnFilterInfo[]=[];

  summarySortInput?: SortInfo[]=[];
  summarySortOutput?: SortInfo[]=[];
  summarySortLookup?: SortInfo[]=[];
  summarySortA?: SortInfo[]=[];
  summarySortB?: SortInfo[]=[];
  summarySortC?: SortInfo[]=[];
  summarySortD?: SortInfo[]=[];
  summarySortE?: SortInfo[]=[];
  summarySortResult?: SortInfo[]=[];

  menuTrigger: MatMenuTrigger=null;
  menuTriggerResult: MatMenuTrigger=null;

  prevSource: string="";

  async refreshSummaryDataFarmPreviewAsync(sender: PreviewTableComponent, isNewCache: boolean = false): Promise<void> {
    const busyIndicator = this.showBusyDialog('Loading Data Preview Summary.');
    try {
      if(this.selectFilterValues.length==0){
        //this.displayWarning("No columns have been selected.","Warning");
      }else{
        const filters = (sender.activeFilterSet ?? []).map(f => {
          const filterInfo = new ColumnFilter();
          filterInfo.field = f.columnName;
          filterInfo.values = f.values;
          return filterInfo;
        });
        const options = new PreviewDataOptions(sender.pageIndex, sender.rowsPerPage);
        options.filters = filters;
        options.sortInfo= sender.activeSortsSet ?? []; 
        var request :GetSummaryDataFarmPreviewRequest | null = null;
        if (sender === this.summaryDataFarmPreviewTableInput) {
          const parentFilters = (this.inputDataFarmPreviewTable.activeFilterSet ?? []).map(f => {
            const filterInfo = new ColumnFilter();
            filterInfo.field = f.columnName;
            filterInfo.values = f.values;
            return filterInfo;
          });
          this.totalSummaryRowsInput="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transInput);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesInput.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableOutput) {
          this.totalSummaryRowsOutput="";
          var cachekeyRequest:GetCacheKeyRequest=null;
          if(this.viewModel.transform.transformType==TransformType.farmJoiner){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerOutput);
          }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterOutput);
          }else{
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutputTemp);
          }
          const parentFilters = (this.outputDataFarmPreviewTable.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });

          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesOutput.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableLookup) {
          this.totalSummaryRowsLookup="";
          const parentFilters = (this.lookupDataFarmPreviewTable.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transLookupInput);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesLookup.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableA) {
          this.totalSummaryRowsA="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputA);
          const parentFilters = (this.previewTableA.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesA.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableB) {
          this.totalSummaryRowsB="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputB);
          const parentFilters = (this.previewTableB.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesB.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableC) {
          this.totalSummaryRowsC="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputC);
          const parentFilters = (this.previewTableC.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesC.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableD) {
          this.totalSummaryRowsD="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputD);
          const parentFilters = (this.previewTableD.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesD.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        } else if (sender === this.summaryDataFarmPreviewTableE) {
          this.totalSummaryRowsE="";
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputE);
          const parentFilters = (this.previewTableE.activeFilterSet ?? []).map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            options,
            this.selectFilterValuesE.join(',') ?? '',isNewCache,SummaryType.DataFarm, parentFilters);
        }  

        const response = await this.configService.getSummaryDataFarm(request);
        if(response.data.totalRows==0){
          //this.totalSummaryRows="";
        //later implement input or output have file or not
        }else{
          const totalRows = Number(response.data.totalSummaryFarmRows).toLocaleString();
          if (sender === this.summaryDataFarmPreviewTableInput) {
            this.totalSummaryRowsInput="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableOutput) {
            this.totalSummaryRowsOutput="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableLookup) {
            this.totalSummaryRowsLookup="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableA) {
            this.totalSummaryRowsA="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableB) {
            this.totalSummaryRowsB="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableC) {
            this.totalSummaryRowsC="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableD) {
            this.totalSummaryRowsD="Total Row Count: "+totalRows;
          } else if (sender === this.summaryDataFarmPreviewTableE) {
            this.totalSummaryRowsE="Total Row Count: "+totalRows;
          }
          // remove 0 should be remove in python later
          const countColumns = response.data.columns
            .filter(col => col.columnName.includes('count'));

          var rowdata=Object.values(response.data.rows);
          rowdata.forEach(row => {
            countColumns.forEach(col => {
              const colIndex = col.index.toString();
              if (row[colIndex] === 0) {
                row[colIndex] = ''; // Replace 0 with blank
              }
            });
          });

          sender.bindData(response.data, 'No Summary preview data');
        }
      }
    } catch (err) {
      this.displayError('Failed to get data farm preview', 'Error', err);
    } finally {
      sender.isGettingData.next(false);
      busyIndicator.close();
    }
  } 
  
  async onApplySelect(info: SelectFilterInfo): Promise<void> {
    this.source=info.source;
    this.selectFilterValues=info.values.filter(cl=>cl!=null).map(col=>col);
    this.menuTrigger.closeMenu();
    this.prevSource=this.source;

    if(info.source == "Input"){
      this.selectFilterValuesInput=[...this.selectFilterValues];
      this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesInput);
      await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableInput,true);
    }else if(info.source == "Output") {
      this.selectFilterValuesOutput=[...this.selectFilterValues];
      this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesOutput);
      await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableOutput,true);
    }else if(info.source == "Lookup") {
      this.selectFilterValuesLookup=[...this.selectFilterValues];
      this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesLookup);
      await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableLookup,true);
    }else if(info.source.includes("Node")) {
      if (info.source === "Node A"){
        this.selectFilterValuesA=[...this.selectFilterValues];
        this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesA);
        await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableA,true);
      }else if (info.source === "Node B"){
        this.selectFilterValuesB=[...this.selectFilterValues];
        this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesB);
        await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableB,true);
      }else if (info.source === "Node C"){
        this.selectFilterValuesC=[...this.selectFilterValues];
        this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesC);
        await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableC,true);
      }else if (info.source === "Node D"){
        this.selectFilterValuesD=[...this.selectFilterValues];
        this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesD);
        await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableD,true);
      }else if (info.source === "Node E"){
        this.selectFilterValuesE=[...this.selectFilterValues];
        this.userData.setSelectFilterValues(await this.GetLocalStorageKey(info.source),this.selectFilterValuesE);
        await this.clearAllSortFilterCache(this.summaryDataFarmPreviewTableE,true);
      }
    }
  }

  async onClearSelect(colName: string): Promise<void> {
    // this.source=this.prevSource;
    // await this.bindSummary(this.source);
    this.menuTrigger.closeMenu();
  }

  onCloseSelect(): void {
    this.menuTrigger.closeMenu();
  }
  async showSelectFilter(menuTrigger: MatMenuTrigger,source:string="Input"): Promise<void> {
    this.source=source;
    this.menuTrigger=menuTrigger;
    //this.menuTrigger.closeMenu();
    if(this.source!=""){
      await this.setPrevSelectFilterValues(this.source);
    }
    await this.bindSummary(this.source);
    //this.changeDet.detectChanges();
    this.menuTrigger.openMenu();
  }

  async onChangeSource(sourcetype:string){
    await this.bindSummary(sourcetype);
  }
  async setPrevSelectFilterValues(sourcetype:string){
    if(sourcetype=="Input"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesInput)];
    }else if(sourcetype=="Output"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesOutput)];
    }else if(sourcetype=="Lookup"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesLookup)];
    }else if(sourcetype.includes("Node")){
      if (sourcetype === "Node A"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesA)];
      }else if (sourcetype === "Node B"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesB)];
      }else if (sourcetype === "Node C"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesC)];
      }else if (sourcetype === "Node D"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesD)];
      }else if (sourcetype === "Node E"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesE)];
      }
    }
  }
  async bindSummary(sourcetype:string): Promise<void> {
    if(sourcetype=="Input"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesInput)];
      if(this.selectValuesInput.length==0){
        await this.bindSelectValues(sourcetype);
      }
      this.selectValues =[...new Set(this.selectValuesInput)];
      this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsInput)];
    }else if(sourcetype=="Output"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesOutput)];
      if(this.selectValuesOutput.length==0){
        await this.bindSelectValues(sourcetype);
      }
      this.selectValues =[...new Set(this.selectValuesOutput)];
      this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsOutput)];
    }else if(sourcetype=="Lookup"){
      this.selectFilterValues=[...new Set(this.selectFilterValuesLookup)];
      if(this.selectValuesLookup.length==0){
        await this.bindSelectValues(sourcetype);
      }
      this.selectValues =[...new Set(this.selectValuesLookup)];
      this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsLookup)];
    }else if(sourcetype.includes("Node")){
      if (sourcetype === "Node A"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesA)];
        if(this.selectValuesA.length==0){
          await this.bindSelectValues(sourcetype);
        }
        this.selectValues =[...new Set(this.selectValuesA)];
        this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsA)];
      }else if (sourcetype === "Node B"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesB)];
        if(this.selectValuesB.length==0){
          await this.bindSelectValues(sourcetype);
        }
        this.selectValues =[...new Set(this.selectValuesB)];
        this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsB)];
      }else if (sourcetype === "Node C"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesC)];
        if(this.selectValuesC.length==0){
          await this.bindSelectValues(sourcetype);
        }
        this.selectValues =[...new Set(this.selectValuesC)];
        this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsC)];
      }else if (sourcetype === "Node D"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesD)];
        if(this.selectValuesD.length==0){
          await this.bindSelectValues(sourcetype);
        }
        this.selectValues =[...new Set(this.selectValuesD)];
        this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsD)];
      }else if (sourcetype === "Node E"){
        this.selectFilterValues=[...new Set(this.selectFilterValuesE)];
        if(this.selectValuesE.length==0){
          await this.bindSelectValues(sourcetype);
        }
        this.selectValues =[...new Set(this.selectValuesE)];
        this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsE)];
      }
    }else if(sourcetype=="Result"){
      if(this.selectValuesResult.length==0){
        await this.bindSelectValues(sourcetype);
      }
      this.selectValuesResult =[...new Set(this.selectValuesResult)];
      this.selectEmptyColumnsResult =[...new Set(this.selectEmptyColumnsResult)];
      this.selectFilterValuesResult=[...new Set(this.selectFilterValuesResult)];
    }
    //this.changeDet.detectChanges();
  }
  async bindSelectValues(sourcetype:string): Promise<void> {
    try {
      var request :GetSummaryDataFarmPreviewRequest | null = null;
      if(sourcetype=="Input"){
        this.selectValuesInput = [...this.inputDataFarmPreviewTable.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
        this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesInput);
        const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transInput);
        request = new GetSummaryDataFarmPreviewRequest(
          cachekeyRequest.userId,
          cachekeyRequest.orgId,
          cachekeyRequest.refineryId,
          cachekeyRequest.nodeId,
          cachekeyRequest.transformId,
          cachekeyRequest.cacheKeyType,
          null,
          this.selectFilterValues.join(',') ?? '');
        const response = await this.configService.getSummaryEmptyColslist(request);
        this.selectEmptyColumnsInput=response.blanks_cols_list;
        this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsInput);
      }else if(sourcetype=="Output"){
        if(this.outputDataFarmPreviewTable.displayedColumnInfos){
          this.selectValuesOutput = [...this.outputDataFarmPreviewTable.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesOutput);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutputTemp);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsOutput=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsOutput);
        }else{
          this.displayWarning("There is no Output Farm, please run the Processor/Ingest Node/Transformer.","Warning");
          this.selectValuesOutput = [...new Set([])];
        }
      }else if(sourcetype=="Lookup"){
        if(this.lookupDataFarmPreviewTable.displayedColumnInfos){
          this.selectValuesLookup = [...this.lookupDataFarmPreviewTable.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesLookup);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transLookupInput);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsLookup=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsLookup);
        }else{
          this.displayWarning("There is no Lookup Farm, please run the Processor/Ingest Node/Transformer.","Warning");
          this.selectValuesLookup = [...new Set([])];
        }
      }else if(sourcetype.includes("Node")){
        if (sourcetype === "Node A"){
          this.selectValuesA = [...this.previewTableA.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesA);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputA);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsA=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsA);
        }else if (sourcetype === "Node B"){
          this.selectValuesB = [...this.previewTableB.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesB);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputB);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsB=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsB);
        }else if (sourcetype === "Node C"){
          this.selectValuesC = [...this.previewTableC.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesC);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputC);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsC=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsC);
        }else if (sourcetype === "Node D"){
          this.selectValuesD = [...this.previewTableD.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesD);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputD);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsD=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsD);
        }else if (sourcetype === "Node E"){
          this.selectValuesE = [...this.previewTableE.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.userData.setSelectValues(await this.GetLocalStorageKey(sourcetype),this.selectValuesE);
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputE);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsE=response.blanks_cols_list;
          this.userData.setSelectEmptyColumns(await this.GetLocalStorageKey(sourcetype),this.selectEmptyColumnsE);
        }
      }else if(sourcetype=="Result"){
        if(this.previewTable.displayedColumnInfos){
          this.selectValuesResult = [...this.previewTable.displayedColumnInfos.map(map => map.name).filter(mp=>mp!='row')];
          this.selectValuesColorResult=[];
          this.selectValuesResult.forEach( i => {
            var colInfo=new  ColumnInfo(0,i,"","",null,false);
            var colInfos=[];
            var color=this.getClassForRowBase(this.previewTable,null,colInfo, colInfos);
            if(!color){color="";}
            this.selectValuesColorResult.push(new ValuesColor(i,color));
          });
          
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transResultTemp);
          request = new GetSummaryDataFarmPreviewRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            null,
            this.selectFilterValues.join(',') ?? '');
          const response = await this.configService.getSummaryEmptyColslist(request);
          this.selectEmptyColumnsResult=response.blanks_cols_list;
        }else{
          this.displayWarning("There is no Output Farm, please run the Processor/Ingest Node/Transformer.","Warning");
          this.selectValuesResult = [...new Set([])];
        }
      }
    }catch (err) {
      this.displayError('Failed to get data Empty column list', 'Error', err);
    } finally {
      //sender.isGettingData.next(false);
    }
  }
  
  async clearFarmPreviewTable(){
    const pvb: PreviewData = {
      columns: [],
      rows: {},
      totalRows: 0,
      totalSummaryFarmRows: 0,
      cacheFileCreationDtTime:''
    };

    //this.summaryDataFarmPreviewTable.bindData(pvb, 'No Summary preview data');
    //this.totalSummaryRows="";
  }
  async refreshSummaryPreviewTable(){
    await this.bindSelectValues("Output");
    //remove 
    this.selectFilterValuesOutput = this.selectFilterValuesOutput.filter(
      sf => this.selectValuesOutput.includes(sf)
    );
    this.selectFilterValuesOutput = this.selectFilterValuesOutput.filter(
      sf => !this.selectEmptyColumnsOutput.includes(sf)
    );
    this.userData.setSelectFilterValues(await this.GetLocalStorageKey("Output"),this.selectFilterValuesOutput);
    await this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableOutput,true);
    // if(this.source=="Output"){
    //   //await this.clearFarmPreviewTable();
    //   // this.selectValues =[...new Set(this.selectValuesOutput)];
    //   // this.selectEmptyColumns =[...new Set(this.selectEmptyColumnsOutput)];
    //   // this.selectFilterValues=[...new Set(this.selectFilterValuesOutput)];
    //   //await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableOutput,true);
      
    // }
    //Result Dashboard
    await this.bindSelectValues("Result");
    //remove 
    this.selectFilterValuesResult = this.selectFilterValuesResult.filter(
      sf => this.selectValuesResult.includes(sf)
    );
    this.selectFilterValuesResult = this.selectFilterValuesResult.filter(
      sf => !this.selectEmptyColumnsResult.includes(sf)
    );
    if(this.sourceResult=="Result"){
      await this.refreshSummaryDataFarmPreviewAsyncResult(this.summaryDataFarmPreviewTableResult,true);
    }
  }

  async showSelectFilterResult(menuTrigger: MatMenuTrigger): Promise<void> {
    this.menuTriggerResult=menuTrigger;
    this.menuTriggerResult.closeMenu();
    var sources = [];
    sources.push("Result");
    this.datafarmSourcesResult = [...new Set(sources)]; 
    this.sourceResult="Result";
    await this.bindSummary(this.sourceResult);
    this.menuTriggerResult.openMenu();
  }
  onApplySelectResult(info: SelectFilterInfo): void {
    this.sourceResult=info.source;
    this.selectFilterValuesResult=info.values.filter(cl=>cl!=null).map(col=>col);
    //this.summaryDataFarmPreviewTableResult.onRefreshClicked();
    this.refreshSummaryDataFarmPreviewAsyncResult(this.summaryDataFarmPreviewTableResult,true);
    this.menuTriggerResult.closeMenu();
    this.selectFilterValuesResult=[...this.selectFilterValuesResult];
  }

  async onClearSelectResult(colName: string): Promise<void> {
    // this.source=this.prevSource;
    // await this.bindSummary(this.source);
    this.menuTriggerResult.closeMenu();
  }

  onCloseSelectResult(): void {
    this.menuTriggerResult.closeMenu();
  }

  async refreshSummaryDataFarmPreviewAsyncResult(sender: PreviewTableComponent, isNewCache: boolean = false): Promise<void> {
    const busyIndicator = this.showBusyDialog('Loading Data Preview Summary.');
    try {
      if(this.selectFilterValuesResult.length==0){
        //this.displayWarning("No columns have been selected in Result Summary.","Warning");
      }else{
        const filters = sender.activeFilterSet?.map(f => {
          const filterInfo = new ColumnFilter();
          filterInfo.field = f.columnName;
          filterInfo.values = f.values;
          return filterInfo;
        });
        
        const parentFilters = (this.previewTable.activeFilterSet ?? []).map(f => {
            const filterInfo = new ColumnFilter();
            filterInfo.field = f.columnName;
            filterInfo.values = f.values;
            return filterInfo;
          });
        const options = new PreviewDataOptions(sender.pageIndex, sender.rowsPerPage);
        options.filters = filters;
        options.sortInfo=this.activeSortsSet;

        var request :GetSummaryDataFarmPreviewRequest = null;
        var cachekeyRequest:GetCacheKeyRequest=null;
        if(this.viewModel.transform.transformType==TransformType.farmJoiner){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerResult);
        }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterResult);
        }else{
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transResultTemp);
        }
        request = new GetSummaryDataFarmPreviewRequest(
          cachekeyRequest.userId,
          cachekeyRequest.orgId,
          cachekeyRequest.refineryId,
          cachekeyRequest.nodeId,
          cachekeyRequest.transformId,
          cachekeyRequest.cacheKeyType,
          options,
          this.selectFilterValuesResult.join(',') ?? '');
        request.isNewCache=isNewCache;
        request.summaryType=SummaryType.Result;
        request.parentFilters = parentFilters;
        const response = await this.configService.getSummaryDataFarm(request);
        if(response.data.totalRows==0){
          this.totalSummaryRowsResult="";
        //later implement input or output have file or not
        }
        else{
          this.totalSummaryRowsResult="Total Row Count: "+response.data.totalSummaryFarmRows.toLocaleString();
          // remove 0 should be remove in python later
          const countColumns = response.data.columns
            .filter(col => col.columnName.includes('count'));

          var rowdata=Object.values(response.data.rows);
          rowdata.forEach(row => {
            countColumns.forEach(col => {
              const colIndex = col.index.toString();
              if (row[colIndex] === 0) {
                row[colIndex] = ''; // Replace 0 with blank
              }
            });
          });

          sender.bindData(response.data, 'No Summary preview data');
        }
      }
    } catch (err) {
      this.displayError('Failed to get data farm preview', 'Error', err);
    } finally {
      sender.isGettingData.next(false);
      busyIndicator.close();
    }
  } 
  onShowFilterResult(event: { trigger: MatMenuTrigger, columnName: string }){
    var parentNodes = this.userData.getNodeParentDataLine()[0].source;
    var selectedNodes = this.userData.getSelectedNodeDetails().id;
    var request :GetFilteredselcolvalsRequest = null;
    const selectedTransformerDetails = this.userData.getSelectedDataTransformConfigViewModel();
    const selectedNodeDetails = this.userData.getSelectedNodeDetails();
    const activeIngestID = selectedTransformerDetails.selectedIngestFarmGuid 
    const transformerGUID = selectedTransformerDetails.activeTransformGuid

    const outputPrevwTransfrmrKey=activeIngestID + "-" + transformerGUID;
    // if(selectedNodeDetails.nodeboxtypename == "Joiner") {
    //   request = new GetFilteredselcolvalsRequest(
    //   this.userData.getUserId(),
    //   this.userData.getOrgId(),
    //   this.userData.getRefineryId(),
    //   this.userData.getNodeId(),
    //   transformerGUID,//this.userData.getTransformId(),
    //   this.userData.getIngestId(),
    //   PreviewType.joinerInput,
    //   event.columnName);
    // }else if(selectedNodeDetails.nodeboxtypename == "Converter") {
    //   request = new GetFilteredselcolvalsRequest(
    //     this.userData.getUserId(),
    //   this.userData.getOrgId(),
    //   this.userData.getRefineryId(),
    //   selectedNodes,//this.userData.getNodeId(),
    //   this.userData.getTransformId(),
    //   this.userData.getIngestId(),
    //   PreviewType.converterInput,
    //   event.columnName);
    // }else{
    //   request = new GetFilteredselcolvalsRequest(
    //     this.userData.getUserId(),
    //   this.userData.getOrgId(),
    //   this.userData.getRefineryId(),
    //   this.userData.getNodeId(),
    //   transformerGUID,//this.userData.getTransformId(),
    //   this.userData.getIngestId(),
    //   PreviewType.transOutput,
    //   event.columnName);
    // }          
    this.summaryDataFarmPreviewTableResult.fetchselectedcolsvalsfrmAPI(request);
  }
  async getDataDictOptionItemDataType(farmtypeid:DataFarmType): Promise<OptionItemDataType[]> {
    var dataDictionaryType=await this.getDataDictionaryType(farmtypeid);
    let dataDictionaryItems: DataDictionaryItem[];
    dataDictionaryItems = this.dataDictService.getColumns(dataDictionaryType);
    let optionitemdatatype:OptionItemDataType[] =dataDictionaryItems.map(dict=>{
      var fieldtype= this.getDataType(dict.PyDataType);
      return new OptionItemDataType(dict.Field,dict.Field,fieldtype);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }
  async getPreviewTableOptionItemDataType(previewtable:PreviewTableComponent): Promise<OptionItemDataType[]> {
    let optionitemdatatype:OptionItemDataType[] =previewtable.displayedColumnInfos.map(col=>{
      var fieldtype= this.getDataType(col.pydataType);
      return new OptionItemDataType(col.name,col.name,fieldtype);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }
  async getDataDictOptionItemNormalizedDataType(farmtypeid:DataFarmType): Promise<OptionItemDataType[]> {
    var dataDictionaryType=await this.getDataDictionaryType(farmtypeid);
    let dataDictionaryItems: DataDictionaryItem[];
    dataDictionaryItems = this.dataDictService.getColumns(dataDictionaryType);
    let optionitemdatatype:OptionItemDataType[] =dataDictionaryItems.map(dict=>{
      var fieldtype= this.getNormalizedDataType(dict.PyDataType);
      return new OptionItemDataType(dict.Field,dict.Field,fieldtype);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }
  async getPreviewTableOptionItemNormalizedDataType(previewtable:PreviewTableComponent): Promise<OptionItemDataType[]> {
    let optionitemdatatype:OptionItemDataType[] =previewtable.displayedColumnInfos.map(col=>{
      var fieldtype= this.getNormalizedDataType(col.pydataType);
      return new OptionItemDataType(col.name,col.name,fieldtype);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }

  async getDataDictOptionItemDataTypePD(farmtypeid:DataFarmType): Promise<OptionItemDataType[]> {
    var dataDictionaryType=await this.getDataDictionaryType(farmtypeid);
    let dataDictionaryItems: DataDictionaryItem[];
    dataDictionaryItems = this.dataDictService.getColumns(dataDictionaryType);
    let optionitemdatatype:OptionItemDataType[] =dataDictionaryItems.map(dict=>{
      return new OptionItemDataType(dict.Field,dict.Field,dict.PyDataType);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }
  async getPreviewTableOptionItemDataTypePD(previewtable:PreviewTableComponent): Promise<OptionItemDataType[]> {
    let optionitemdatatype:OptionItemDataType[] =previewtable.displayedColumnInfos.map(col=>{
      return new OptionItemDataType(col.name,col.name,col.pydataType);
    })
    //dataDictionaryItems.sort((a, b) => a < b ? -1 : 0);
    return optionitemdatatype;
  }
  
  refineryName:string;
  aboutlink:string;
  refineryConfigGuid:string;
  configGuid:string;
  transformNameType:string;
  nodeIndex:string;
  nodeName:string;
  nodeColor:string;
  iconpath:string;
  transformslists:TransformerViewModel[]=[];
  viewModelExport: ExportViewModel;
  async setTransformerDetails(transformType:TransformType): Promise<void>{
    const selectedRconfig=this.userData.getSelectedRefineryDetails();
    const selectedNode=this.userData.getSelectedNodeDetails();
    this.refineryName=selectedRconfig.name;
    this.refineryConfigGuid=selectedRconfig.guid;
    this.configGuid=selectedNode.configGuid;
    this.nodeIndex=selectedNode.rcNo;
    this.nodeName=selectedNode.name;
    this.nodeColor=selectedNode.nodeColor;
    //export - assigned first so the export panel always has a valid view model,
    //even if the master config lookup below fails.
    this.viewModelExport=new ExportViewModel(this.refineryConfigGuid,selectedNode.id,this.transformViewModel.guid,',','.csv',"File",'Transformer',[],
      this.userData.getUserId(),
      this.userData.getOrgId(),
      this.userData.getRefineryId(),
      //this.userData.getNodeId(),
      this.userData.getTransformId(),
      CacheKeyType.tempTransformer);

    // Self-heal, 2026-10-02: getMasterTransformers() is normally populated once at login
    // by AppComponent.ensureMasterConfigsResolved(), but a transform-detail page can still
    // initialize before that async fetch resolves (or after a prior fetch failure), and an
    // unguarded .find() on an undefined array crashed here with a cryptic
    // "Cannot read properties of undefined (reading 'find')" -- surfacing through whichever
    // caller's generic catch happened to be running at the time, with a misleading message
    // unrelated to the actual cause. Fetch directly here rather than crash every transform
    // type's shared initialization on a timing race.
    let allMasterTransformers = this.userData.getMasterTransformers();
    if(!allMasterTransformers || allMasterTransformers.length === 0){
      const masterconfigs = await this.configService.getMasterConfigs();
      this.userData.setMasterNodeBoxTypes(masterconfigs.nodeboxtypes);
      this.userData.setMasterTransformers(masterconfigs.transformers);
      this.userData.setMasterFarmtypes(masterconfigs.farmtypes);
      allMasterTransformers = masterconfigs.transformers;
    }
    const masterconfig=allMasterTransformers.find(x=>x.id==transformType);
    if(!masterconfig){
      throw new Error(`Master config not found for transform type '${TransformTypeUtil.toString(transformType)}'. The master config list may be out of date - try reloading the app.`);
    }
    this.transformNameType=` (${TransformTypeUtil.toString(transformType)})`;
    this.aboutlink=masterconfig.aboutlink;
    var transformiconpath=masterconfig.transformiconpath;
    this.iconpath=transformiconpath.replace("##",masterconfig.transformer+"-"+selectedNode.farmtypename);
    if(transformType !== TransformType.farmJoiner && transformType !== TransformType.farmConverter){
      const selectedNodeId=selectedNode.id;
      const transforms = this.dataTransformConfigViewModel.transforms;
      this.transformslists=[];
      transforms.forEach(transform=>{
        if(transform.nodeID==selectedNodeId){
          this.transformslists.push(transform);
        }
      })
      this.transformslists.sort((a, b) => a.index - b.index);
    }

    // Smart Sample applies to every row-based transformer, not just the types
    // with a registered Copilot agent — must run unconditionally, unlike
    // _initCopilotContext() below (which bails out for types with no agent).
    this._initSmartSampleSync();

    // Register Copilot context if this transformer supports it
    this._initCopilotContext();
  }

  /**
   * Wires the Smart Sample "Apply" pipeline (sampleRangeUpdate$ → viewModel)
   * and restores the green "current" state after navigation — independent of
   * Copilot AI-chat registration (getCopilotTransformType()). Previously lived
   * inside _initCopilotContext() and silently never subscribed for any type
   * without a registered Copilot agent (ActivityNamer, EventNamer,
   * EventGroupNamer, GroupAttributor, RowFiller): Smart Sample's dialog would
   * compute a recommendation, but Apply had no subscriber listening, so
   * nothing ever reached viewModel.parameters.sampleStart/sampleEnd.
   */
  private _initSmartSampleSync(): void {
    // sampleRangeUpdate$ lives on the root-singleton CopilotContextService, so an
    // untracked subscription here survives for the app's lifetime, keeping this
    // whole component instance (and its viewModel) unreachable-by-GC forever after
    // Angular destroys it — confirmed via a heap snapshot showing hundreds of leaked
    // instances of this component after normal navigation. Must be unsubscribed in
    // ngOnDestroy like every sibling subscription in this class.
    this._sampleRangeUpdateSub?.unsubscribe();
    this._sampleRangeUpdateSub = this._copilotCtx.sampleRangeUpdate$.subscribe(update => {
      const vm = this.viewModel as any;
      if (update.sampleStart !== undefined) vm.sampleStart = update.sampleStart;
      if (update.sampleEnd   !== undefined) vm.sampleEnd   = update.sampleEnd;
      if (update.sortBy)                    vm.sortBy      = update.sortBy;
      // Also update nested parameters (used by Row Extractor and Column Calc)
      if (vm.parameters) {
        if (update.sampleStart !== undefined) vm.parameters.sampleStart = update.sampleStart;
        if (update.sampleEnd   !== undefined) vm.parameters.sampleEnd   = update.sampleEnd;
      }
      // Track the Copilot-set range so the Smart Sample button can go green
      if (update.sampleStart !== undefined) this._copilotSampleStart = update.sampleStart;
      if (update.sampleEnd   !== undefined) this._copilotSampleEnd   = update.sampleEnd;
    });

    // Restore Smart Sample green state after navigation — the Subject doesn't replay,
    // so the service stores the last applied range keyed by transformId. Uses
    // configGuid directly (available for every type) rather than
    // context$.value?.transformId (only set for types with a registered
    // Copilot agent — context$.value.transformId is always configGuid anyway
    // when it does exist, see _initCopilotContext() below).
    const _restored = this._copilotCtx.getRestoredSampleRange(this.configGuid ?? '');
    if (_restored) {
      if (_restored.sampleStart !== undefined) this._copilotSampleStart = _restored.sampleStart;
      if (_restored.sampleEnd   !== undefined) this._copilotSampleEnd   = _restored.sampleEnd;
    }
  }

  private _initCopilotContext(): void {
    const transformType = this.getCopilotTransformType();
    if (transformType === null) return;

    this._copilotCtx.set({
      transformType,
      transformId:    this.configGuid,
      refineryId:     this.refineryConfigGuid,
      transformLabel: TransformTypeUtil.toString(transformType as TransformType),
      transformName:  this.dataTransformConfigViewModel.name,
      currentConfig: this.getCopilotConfig(),
      columnNames:   this.getCopilotColumnNames(),
      constraints:   this.getCopilotConstraints(),
    });

    // Register live config getter — always returns the current View Config JSON
    this._copilotCtx.getLiveConfig = () => this.getCopilotConfig();

    // Register DTR pre-run for Run Full Analysis (subclasses override to set runAll = true)
    this._copilotCtx.runForAnalysis = () => this._runForCopilotAnalysis();

    // Wire config-apply events back to this transformer.
    // setTimeout defers the mutation out of Angular's change detection cycle,
    // preventing NG0100 ExpressionChangedAfterItHasBeenCheckedError.
    this._copilotEventSub?.unsubscribe();
    this._copilotEventSub = this._copilotCtx.configEvent$.subscribe(evt => {
      if (evt.transformId !== this.configGuid) return;
      setTimeout(() => {
        // Copilot modifies the config without saving — any prior compiled state is stale.
        this.compiledConfigHash = null;
        this.compiledAt = null;
        this.applyCopilotConfig(evt.config, evt.mode, evt.skipRun);
      });
    });

    this._rulesSortedSub?.unsubscribe();
    this._rulesSortedSub = this._copilotCtx.rulesSorted$.subscribe(evt => {
      if (evt.transformId !== this.configGuid) return;
      // Suppress the first automatic resort after a Copilot run: the simulation-based
      // pre-sort is already optimal; resorting on cache-hit stats causes unnecessary cache
      // misses. User-initiated sorts (clicking Apply in the advisor) always go through.
      if (this._skipNextSortedRules && !evt.userInitiated) { this._skipNextSortedRules = false; return; }
      this._skipNextSortedRules = false;
      this.applySortedRules(evt.rules);
    });

    this._copilotNavSub?.unsubscribe();
    this._copilotNavSub = this._copilotCtx.navigateTo$.subscribe(hint => {
      if (hint.destination === 'summary_fields') {
        const doNavigate = () => {
          if (hint.fields?.length) {
            this.selectFilterValuesResult = [...hint.fields];
          }
          this.isResultsPanelExpanded   = true;
          this.resultsDashboardTabIndex = 1;
          setTimeout(() => {
            this.refreshSummaryDataFarmPreviewAsyncResult(this.summaryDataFarmPreviewTableResult, true);
          }, 310);
        };
        if (this._dtrRunning) {
          // DTR is in flight — wait for it to finish before showing Results Summary
          this._dtrRunComplete$.pipe(take(1)).subscribe(() => doNavigate());
          this.snackBar.open('DTR running — Results Summary will open when complete.', '', { duration: 4000 });
        } else {
          doNavigate();
        }
      }
    });

    this._copilotAnalysisSub?.unsubscribe();
    this._copilotAnalysisSub = this._copilotCtx.analysisPreview$.subscribe(data => {
      this.copilotStats         = data?.copilotStats       ?? [];
      this.copilotLog           = data?.copilotLog         ?? [];
      this.copilotContamStats   = data?.copilotContamStats ?? [];
      this._copilotSessionId    = data?.sessionId          ?? null;
      this._copilotFilterValuesCache.clear();
      this._lastCopilotAnalysisData = data;
      this.onAnalysisPreviewReceived(data);
      setTimeout(() => this.bindCopilotPreviewIfReady());
    });

    this._copilotAuditSub?.unsubscribe();
    this._copilotAuditSub = this._copilotCtx.runAudit$.subscribe(() => {
      this._triggerAuditRun();
    });

    this._copilotBpmScoreSub?.unsubscribe();
    this._copilotBpmScoreSub = this._copilotCtx.bpmScore$.subscribe(data => {
      this._upsertCopilotStat(
        'Score',
        `${data.general.score} / ${data.general.max_auto}`,
        `Config quality: ${data.general.score}/${data.general.max_auto} structural checks passed.`
      );
      if (data.domain) {
        this._upsertCopilotStat(
          data.domain.name,
          `${data.domain.score} / ${data.domain.max_total}`,
          `${data.domain.name}: ${data.domain.score}/${data.domain.max_auto} auto-checks + ${data.domain.max_total - data.domain.max_auto} manual checks.`
        );
      }
    });
  }

  /** Controls visibility of the Results Dashboard expansion panel. */
  protected isResultsPanelExpanded: boolean = false;
  /** Selected tab index in the Results Dashboard tab group (0=Results, 1=Summary, 2=DTR Copilot). */
  protected resultsDashboardTabIndex: number = 0;

  /** Generic stats for the DTR Copilot tab header. Shape: { label, value, width?, tooltip? }[] */
  copilotStats: { label: string; value: string; width?: string; tooltip?: string }[] = [];

  /** Optimisation log lines from the hill-climbing optimizer. */
  copilotLog: string[] = [];

  /** Controls visibility of the optimisation log panel in the Copilot tab. */
  showCopilotLog = false;

  /** Contamination summary stats projected into the preview table header. */
  copilotContamStats: { label: string; value: string; width?: string; tooltip?: string }[] = [];

  /**
   * Last DTR execution time through the generic (uncompiled) engine, formatted as
   * "Xm Ys". Computed as pure Flask wall-clock time inside runcurrenttransformer's
   * streaming generator (engineController.py) — corrected 2026-08-30; this is NOT
   * a .NET-server-provided value despite earlier documentation/comments here and
   * in this component's own template — the .NET API (port 4000) is not in this
   * request's path at all, only Flask (port 5000, activeEngineBaseUrl).
   */
  lastDtrRunTime: string = '';

  /**
   * Last DTR execution time through the compiled fast-path engine
   * (row_extractor_compiled.py) — the same engine node/refinery-level execution
   * actually uses in production. "NA" (not a duration) when the config isn't
   * fully compilable, or its own Run All Rows setting is off — see
   * runcurrenttransformer's compiledTransformerTime field.
   */
  lastCompiledRunTime: string = '';

  /**
   * Actual thread/chunk count used for the last run of each engine -- the
   * Architect, 2026-09-30: "when auto-threading is invoked I cannot see the
   * actual thread count." Auto (Threads=0) can pick a different count for
   * each engine, so both are tracked separately rather than one shared value.
   */
  lastDtrThreadsUsed: number | null = null;
  lastCompiledThreadsUsed: number | null = null;

  /** Last data received from analysisPreview$ — held so the copilot tab can bind it on first render. */
  protected _lastCopilotAnalysisData: any = null;

  bindCopilotPreviewIfReady(): void {
    if (!this._lastCopilotAnalysisData || !this.copilotPreviewTable) return;
    this.copilotPreviewTable.bindData(this._lastCopilotAnalysisData, 'No data').then(() => {
      this._applyCopilotColumnWidths();
    });
  }

  private async _refreshCopilotContaminants(sender: PreviewTableComponent): Promise<void> {
    if (!this._copilotSessionId) return;
    const userId   = this.userData.getUserId();
    const filters  = sender.activeFilterSet?.map(f => ({ field: f.columnName, values: f.values })) ?? [];
    const sortInfo = sender.activeSortsSet?.map(s => ({ sortField: s.sortField, sortAscending: s.sortAscending })) ?? [];
    const opts = {
      pageIndex:   sender.pageIndex,
      rowsPerPage: sender.rowsPerPage,
      filters,
      sortInfo,
    };
    try {
      const resp = await firstValueFrom(this._copilotSvc.getContaminants(this._copilotSessionId, userId, opts));
      await sender.bindData(resp, 'No contamination data');
      this._applyCopilotColumnWidths();
    } catch (ex) {
      console.error('Failed to refresh contaminants:', ex);
    }
  }

  private _formatDtrRunTime(raw: string): string {
    // .NET TimeSpan: [d.]HH:mm:ss[.fffffff]
    const colonParts = raw.split(':');
    if (colonParts.length < 3) return raw;
    const hours        = parseInt(colonParts[0], 10) || 0;
    const minutes      = parseInt(colonParts[1], 10) || 0;
    const totalMinutes = hours * 60 + minutes;
    const seconds      = parseFloat(colonParts[2]).toFixed(2);
    return totalMinutes > 0 ? `${totalMinutes}m ${seconds}s` : `${seconds}s`;
  }

  private _parseDtrRunTimeMs(raw: string): number {
    const colonParts = raw.split(':');
    if (colonParts.length < 3) return 0;
    const hours   = parseInt(colonParts[0], 10) || 0;
    const minutes = parseInt(colonParts[1], 10) || 0;
    const secs    = parseFloat(colonParts[2]) || 0;
    return (hours * 3600 + minutes * 60 + secs) * 1000;
  }

  private _upsertDtrRunStat(): void {
    this._upsertCopilotStat(
      'Uncompiled', this.lastDtrRunTime,
      'Run Time (uncompiled) — time taken by the generic engine to execute this transform.'
    );
    this._upsertCopilotStat(
      'Threads (uncompiled)', this.lastDtrThreadsUsed != null ? String(this.lastDtrThreadsUsed) : 'NA',
      'Actual thread/chunk count used for the run above — shows what Threads=0 (auto) actually chose, since that value isn\'t otherwise visible.'
    );
    this._upsertCopilotStat(
      'When compiled', this.lastCompiledRunTime,
      'Preview only — how fast the compiled fast-path engine would run this transform\'s CURRENT config (saved or not), not necessarily what production is using right now. Save to make this the actual compiled plan production runs. NA when the config isn\'t fully compilable or its own Run All Rows setting is off.'
    );
    this._upsertCopilotStat(
      'Threads (when compiled)', this.lastCompiledThreadsUsed != null ? String(this.lastCompiledThreadsUsed) : 'NA',
      'Actual thread/chunk count used for the compiled preview run above — shows what Threads=0 (auto) actually chose. Always 1 when the config has Find Phrases rules or the farm is too small to parallelize.'
    );
  }

  private _upsertCopilotStat(label: string, value: string, tooltip: string): void {
    const w       = Math.max(label.length * 7 + 40, (value?.length || 0) * 9 + 28);
    const entry   = { label, value, width: `${w}px`, tooltip };
    const updated = [...this.copilotStats];
    const idx     = updated.findIndex(s => s.label === label);
    if (idx >= 0) { updated[idx] = entry; } else { updated.push(entry); }
    this.copilotStats = updated;
  }

  /** Tracks the last Copilot-recommended sample range (1-based) — drives Smart Sample green state. */
  protected _copilotSampleStart: number | null = null;
  protected _copilotSampleEnd:   number | null = null;

  /** True when the current viewModel sample range matches the last Copilot-set Smart Sample range. */
  get smartSampleIsCurrent(): boolean {
    if (this._copilotSampleStart === null || this._copilotSampleEnd === null) return false;
    const vm = this.viewModel as any;
    const cur = vm?.parameters ?? vm;
    return cur?.sampleStart === this._copilotSampleStart && cur?.sampleEnd === this._copilotSampleEnd;
  }

  /** Input cache key for the current transformer — used by Smart Sample.
   * Independent of Copilot AI-chat registration (getCopilotTransformType()):
   * Smart Sample applies to every row-based transformer, not just the types
   * with a registered Copilot agent, so this must not depend on
   * _copilotCtx.context$ being non-null (that also gates the global Copilot
   * chat panel's own visibility — see app.component.html). Set alongside
   * _copilotCtx.update() wherever the input cache key is computed. */
  protected _smartSampleInputCacheKey = '';
  get copilotInputCacheKey(): string {
    return this._smartSampleInputCacheKey;
  }

  /** Apply a smart sample range result from the top-header dialog. */
  applySmartSample(update: { sampleStart: number; sampleEnd: number; sortBy?: string }): void {
    this._copilotCtx.applySampleRange(update);
  }

  /** Reflects a just-completed Audit Compiler Quality result immediately -- the backend
   *  already persisted these onto the transform's saved config (best-effort); updating
   *  the bound @Input()s here means the top header's icon goes blue/green/red right away
   *  instead of staying on its pre-audit state until some unrelated refresh/navigation
   *  re-fetches them.
   *
   *  Root cause, 2026-10-01 (the Architect: navigating away and back reverted BOTH green
   *  and red states to blue): this only ever updated these two properties on the live
   *  component instance, which navigation destroys. applyCompiledStateFrom() -- called by
   *  every newly-constructed instance -- re-derives lastAuditHash/lastAuditResult from
   *  configViewModelService's shared, cross-navigation DataTransformConfigViewModel
   *  singleton, not from this component. compiledConfigHash survives navigation only
   *  because saveConfigImpl() refreshes that same shared singleton with the fresh
   *  post-save server response (setDataTransformConfigViewModel(new
   *  DataTransformConfigViewModel(config))) -- Audit had no equivalent step. Fixed by
   *  also writing these fields onto the shared singleton's own matching transform entry
   *  and re-publishing it, mirroring the exact pattern already used elsewhere in this
   *  file for a same-type navigation (dataTransformConfigViewModel.transforms.find(...)
   *  then setDataTransformConfigViewModel(...)). */
  onAuditCompleted(result: { lastAuditHash: string; lastAuditResult: string }): void {
    this.lastAuditHash = result.lastAuditHash;
    this.lastAuditResult = result.lastAuditResult;

    const editedGuid = this.transformViewModel?.transform?.guid;
    const sharedTransform: any = this.dataTransformConfigViewModel?.dataTransformerConfig?.transforms
      ?.find((t: any) => t.guid === editedGuid);
    if (sharedTransform) {
      sharedTransform.lastAuditHash = result.lastAuditHash;
      sharedTransform.lastAuditResult = result.lastAuditResult;
      this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
      this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);
    }
  }

  /** Override in subclasses to expand the results panel when Copilot loads analysis data. */
  protected onAnalysisPreviewReceived(data?: any): void {}

  private _applyCopilotColumnWidths(): void {
    const table = this.copilotPreviewTable;
    if (!table?.dataSource || !table.displayedColumnInfos) return;

    const CHR     = 8;   // px per character
    const PAD     = 24;  // cell padding
    const CAP_CH  = 100; // max characters for resizable columns

    const rows: any[] = table.dataSource.data;
    const widths: { [index: number]: { width: string; fixed: boolean } } = {};

    table.displayedColumnInfos.forEach(col => {
      const headerChars = col.name.length;

      if (col.index === 0) {
        // Row: size from data so full row numbers are visible; add room for filter icon
        const maxDataChars = rows.reduce((max, row) => Math.max(max, String(row[0] ?? '').length), 0);
        const chars = Math.max(headerChars, maxDataChars);
        widths[col.index] = { width: `${chars * CHR + PAD + 24}px`, fixed: true };
      } else if (col.index === 1) {
        // Rule No: fixed at header width
        widths[col.index] = { width: `${headerChars * CHR + PAD}px`, fixed: true };
      } else {
        const maxDataChars = rows.reduce((max, row) => {
          return Math.max(max, String(row[col.index] ?? '').length);
        }, 0);
        const chars = Math.min(Math.max(headerChars, maxDataChars) + 4, CAP_CH);
        widths[col.index] = { width: `${chars * CHR + PAD}px`, fixed: false };
      }
    });

    table.setColumnWidths(widths);
  }

  ngOnDestroy(): void {
    this._copilotEventSub?.unsubscribe();
    this._rulesSortedSub?.unsubscribe();
    this._copilotAnalysisSub?.unsubscribe();
    this._copilotNavSub?.unsubscribe();
    this._copilotAuditSub?.unsubscribe();
    this._copilotBpmScoreSub?.unsubscribe();
    this._sampleRangeUpdateSub?.unsubscribe();
    this._configVmSub?.unsubscribe();
    this._copilotCtx.clear();
  }
  async refreshPreviewTable(sender: PreviewTableComponent): Promise<void> {
    // Download the data for the previewTable
    if (sender === this.inputDataFarmPreviewTable) {
      await this.refreshInputDataFarmPreviewAsync(sender);
    }else if (sender === this.outputDataFarmPreviewTable) {
      await this.runCurrentTransform(this.previewTable.activeFilterSet, this.outputDataFarmPreviewTable.activeFilterSet, false);
    }else if (sender === this.previewTable){
      await this.refreshResultsPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableInput){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableOutput){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableLookup){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableA){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableB){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableC){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableD){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableE){
      await this.refreshSummaryDataFarmPreviewAsync(sender);
    }else if (sender === this.summaryDataFarmPreviewTableResult){
      await this.refreshSummaryDataFarmPreviewAsyncResult(sender);
    }else if (sender === this.auditPreviewTable) {
      await this._refreshAuditTrailPage(sender);
    }
  }

  protected async _refreshAuditTrailPage(sender: PreviewTableComponent): Promise<void> {
    if (!this._copilotSessionId) return;
    const sorts   = sender.activeSortsSet ?? [];
    const filters = (sender.activeFilterSet ?? []).map(f => ({ field: f.columnName, values: f.values }));
    const result  = await firstValueFrom(
      this._copilotSvc.getAuditData(
        this._copilotSessionId, this.userData.getUserId(),
        sender.pageIndex, sender.rowsPerPage, sorts, filters,
      )
    );
    if (result) {
      const pd = new PreviewData();
      pd.columns               = result.columns as any;
      pd.rows                  = result.rows;
      pd.totalRows             = result.totalRows;
      pd.filterInfo            = result.filterInfo;
      pd.cacheFileCreationDtTime = '';
      await sender.bindData(pd, 'No audit data');
      sender.totalRows = result.totalRows;
      this._applyAuditColumnWidths();
    }
  }

  protected _applyAuditColumnWidths(): void {
    const table = this.auditPreviewTable;
    if (!table?.displayedColumnInfos) return;
    const widths: { [index: number]: { width: string; fixed: boolean } } = {};
    for (const col of table.displayedColumnInfos) {
      if (col.name === 'Row') {
        widths[col.index] = { width: '55px', fixed: true };
      } else if (col.name.endsWith(' Rule')) {
        widths[col.index] = { width: '75px', fixed: true };
      }
    }
    table.setColumnWidths(widths);
  }
  //Preview Table Delegate
  async onRefreshClicked(sender: PreviewTableComponent): Promise<void> {
    await this.refreshPreviewTable(sender);
  }

  async onPageChanged(sender: PreviewTableComponent,totalRows: number,pageSize: number,pageIndex: number): Promise<number> {
    if (sender === this.copilotPreviewTable) {
      await this._refreshCopilotContaminants(sender);
      return totalRows;
    }
    if (sender === this.auditPreviewTable) {
      await this._refreshAuditTrailPage(sender);
      return totalRows;
    }
    // Paging never changes Result Summary stats — skip the expensive getSummaryDataFarm call.
    this._skipResultSummaryRefresh = sender === this.previewTable || sender === this.outputDataFarmPreviewTable;
    const busyIndicator = this.showBusyDialog('Please wait...');
    try {
      await this.refreshPreviewTable(sender);
    } finally {
      this._skipResultSummaryRefresh = false;
      busyIndicator.close();
    }
    busyIndicator.close();
    return totalRows;
  }

  async onShowFilter(sender: PreviewTableComponent, columnName: string): Promise<void> {

    if (sender === this.auditPreviewTable) {
      if (!this._copilotSessionId) return;
      const cachedAudit = this._auditFilterValuesCache.get(columnName);
      if (cachedAudit) {
        sender.setClientFilterValues(columnName, cachedAudit);
        return;
      }
      this._copilotSvc.getAuditData(
        this._copilotSessionId, this.userData.getUserId(),
        0, 10, [], [], columnName,
      ).subscribe({
        next: resp => {
          const vals = resp?.filterValues ?? [];
          this._auditFilterValuesCache.set(columnName, vals);
          sender.setClientFilterValues(columnName, vals);
        },
        error: () => sender.setClientFilterValues(columnName, []),
      });
      return;
    }
    if (sender === this.copilotPreviewTable) {
      if (!this._copilotSessionId) return;
      const cached = this._copilotFilterValuesCache.get(columnName);
      if (cached) {
        sender.setClientFilterValues(columnName, cached);
        return;
      }
      const busy = this.showBusyDialog('Please wait...');
      this._copilotSvc.getContaminantValues(this._copilotSessionId, this.userData.getUserId(), columnName)
        .subscribe({
          next: resp => {
            const values = resp.values ?? [];
            this._copilotFilterValuesCache.set(columnName, values);
            busy.close();
            sender.setClientFilterValues(columnName, values);
          },
          error: () => busy.close(),
        });
      return;
    }
    if(sender.activeFilterSet == undefined || sender.activeFilterSet.length == 0) {
      const busyIndicator = this.showBusyDialog('Please wait...');
        var cachekeyRequest:GetCacheKeyRequest=null;
        
        if(sender === this.inputDataFarmPreviewTable) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transInput);
        }else if (sender === this.outputDataFarmPreviewTable) {
          if(this.viewModel.transform.transformType==TransformType.farmJoiner){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerOutput);
          }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterOutput);
          }else{
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutputTemp);
          }
        }else if (sender === this.lookupDataFarmPreviewTable) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transLookupInput);
        }else if (sender === this.previewTable) {
          if(this.viewModel.transform.transformType==TransformType.farmJoiner){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerResult);
          }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterResult);
          }else{
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transResultTemp);
          }
        }else if (sender === this.summaryDataFarmPreviewTableInput) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transInput);
        }else if (sender === this.summaryDataFarmPreviewTableOutput) {
          if(this.viewModel.transform.transformType==TransformType.farmJoiner){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerOutput);
          }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterOutput);
          }else{
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutputTemp);
          }
        }else if (sender === this.summaryDataFarmPreviewTableLookup) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transLookupInput);
        }else if (sender === this.summaryDataFarmPreviewTableA) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputA);
        }else if (sender === this.summaryDataFarmPreviewTableB) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputB);
        }else if (sender === this.summaryDataFarmPreviewTableC) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputC);
        }else if (sender === this.summaryDataFarmPreviewTableD) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputD);
        }else if (sender === this.summaryDataFarmPreviewTableE) {
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputE);
        }else if (sender === this.summaryDataFarmPreviewTableResult) {
          if(this.viewModel.transform.transformType==TransformType.farmJoiner){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerResult);
          }else if(this.viewModel.transform.transformType==TransformType.farmConverter){
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.converterResult);
          }else{
            cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transResultTemp);
          }
        }else if (sender === this.previewTableA){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputA);
        }else if (sender === this.previewTableB){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputB);
        }else if (sender === this.previewTableC){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputC);
        }else if (sender === this.previewTableD){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputD);
        }else if (sender === this.previewTableE){
          cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.joinerInputE);
        }
        
        try{
          var request = new GetFilteredselcolvalsRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType,
            columnName);
          
          await sender.fetchselectedcolsvalsfrmAPI(request);

        } catch(ex) {
          console.error('Error fetching data:', ex);
          this.displayError('Failed to fetch filter values. Please try again later.',"Failed to fetch");
        }finally {
          busyIndicator.close();
        }
    } else {
        sender.applyStackFilters(columnName);
    }   
  }

  async onApplyFilter(sender: PreviewTableComponent, activeFilterSet: ColumnFilterInfo[],  info: ColumnFilterInfo): Promise<void> {
    if (sender === this.copilotPreviewTable) {
      await this._refreshCopilotContaminants(sender);
      return;
    }
    const busyIndicator = this.showBusyDialog('Please wait...');
    if (sender === this.summaryDataFarmPreviewTableInput){
      this.summaryFilterInput=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterInput);
      //this.summaryDataFarmPreviewTableInput.activeFilterSet=(this.summaryFilterInput ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableOutput){
      this.summaryFilterOutput=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Output"),this.summaryFilterOutput);
      //this.summaryDataFarmPreviewTableOutput.activeFilterSet=(this.summaryFilterOutput ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableLookup){
      this.summaryFilterLookup=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Lookup"),this.summaryFilterLookup);
      //this.summaryDataFarmPreviewTableLookup.activeFilterSet=(this.summaryFilterLookup ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableA){
      this.summaryFilterA=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node A"),this.summaryFilterA);
      //this.summaryDataFarmPreviewTableA.activeFilterSet=(this.summaryFilterA ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableB){
      this.summaryFilterB=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node B"),this.summaryFilterB);
      //this.summaryDataFarmPreviewTableB.activeFilterSet=(this.summaryFilterB ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableC){
      this.summaryFilterC=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node C"),this.summaryFilterC);
      //this.summaryDataFarmPreviewTableC.activeFilterSet=(this.summaryFilterC ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableD){
      this.summaryFilterD=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node D"),this.summaryFilterD);
      //this.summaryDataFarmPreviewTableD.activeFilterSet=(this.summaryFilterD ?? []);
    }else if (sender === this.summaryDataFarmPreviewTableE){
      this.summaryFilterE=sender.activeFilterSet;
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node E"),this.summaryFilterE);
      //this.summaryDataFarmPreviewTableE.activeFilterSet=(this.summaryFilterE ?? []);
    }
    await this.refreshPreviewTable(sender);
    busyIndicator.close();
  }

  async onClearFilter(sender: PreviewTableComponent, activeFilterSet: ColumnFilterInfo[], columnName: string): Promise<void> {
    if (sender === this.copilotPreviewTable) {
      await this._refreshCopilotContaminants(sender);
      return;
    }
    const busyIndicator = this.showBusyDialog('Please wait...');
    if(sender === this.summaryDataFarmPreviewTableInput) {
      this.summaryFilterInput=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterInput);
    }else if(sender === this.summaryDataFarmPreviewTableOutput) {
      this.summaryFilterOutput=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Output"),this.summaryFilterOutput);
    }else if(sender === this.summaryDataFarmPreviewTableLookup) {
      this.summaryFilterLookup=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Lookup"),this.summaryFilterLookup);
    }else if(sender === this.summaryDataFarmPreviewTableA) {
      this.summaryFilterA=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterA);
    }else if(sender === this.summaryDataFarmPreviewTableB) {
      this.summaryFilterB=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterB);
    }else if(sender === this.summaryDataFarmPreviewTableC) {
      this.summaryFilterC=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterC);
    }else if(sender === this.summaryDataFarmPreviewTableD) {
      this.summaryFilterD=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterD);
    }else if(sender === this.summaryDataFarmPreviewTableE) {
      this.summaryFilterE=[];
      this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterE);
    }
    await this.refreshPreviewTable(sender);
    busyIndicator.close();
  }

  async onApplySort(sender: PreviewTableComponent, sortData: SortInfo[]): Promise<void> {
    if (sender === this.copilotPreviewTable) {
      await this._refreshCopilotContaminants(sender);
      return;
    }
    if (sender === this.auditPreviewTable) {
      return;  // sort state is in sender.activeSortsSet; refresh fires via onRefreshClicked
    }
    if(sender === this.previewTable) {
      this.activeResultSortsSet = sortData;
    }else{
      this.activeSortsSet = sortData;
      if (sender === this.summaryDataFarmPreviewTableInput){
        this.summarySortInput=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Input"),this.summarySortInput);
      }else if (sender === this.summaryDataFarmPreviewTableOutput){
        this.summarySortOutput=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Output"),this.summarySortOutput);
      }else if (sender === this.summaryDataFarmPreviewTableLookup){
        this.summarySortLookup=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Lookup"),this.summarySortLookup);
      }else if (sender === this.summaryDataFarmPreviewTableA){
        this.summarySortA=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node A"),this.summarySortA);
      }else if (sender === this.summaryDataFarmPreviewTableB){
        this.summarySortB=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node B"),this.summarySortB);
      }else if (sender === this.summaryDataFarmPreviewTableC){
        this.summarySortC=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node C"),this.summarySortC);
      }else if (sender === this.summaryDataFarmPreviewTableD){
        this.summarySortD=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node D"),this.summarySortD);
      }else if (sender === this.summaryDataFarmPreviewTableE){
        this.summarySortE=sender.activeSortsSet;
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node E"),this.summarySortE);
      }
    }
  }

  async onClearAllSortFilter(sender: PreviewTableComponent): Promise<void> {
    if (sender === this.copilotPreviewTable) {
      await this._refreshCopilotContaminants(sender);
      return;
    }
    if(sender === this.previewTable) {
      this.activeResultSortsSet = [];
    }else{
      this.activeSortsSet = [];
      if(sender === this.summaryDataFarmPreviewTableInput){
        this.summaryFilterInput=[];
        this.summarySortInput=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Input"),this.summaryFilterInput);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Input"),this.summarySortInput);
        this.clearAllSortFilterCache(sender,false);
      }else if(sender === this.summaryDataFarmPreviewTableOutput) {
        this.summaryFilterOutput=[];
        this.summarySortOutput=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Output"),this.summaryFilterOutput);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Output"),this.summarySortOutput);
        this.clearAllSortFilterCache(sender,false);
      }else if(sender === this.summaryDataFarmPreviewTableLookup) {
        this.summaryFilterLookup=[];
        this.summarySortLookup=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Lookup"),this.summaryFilterLookup);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Lookup"),this.summarySortLookup);
        this.clearAllSortFilterCache(sender,false);
      }else if(sender === this.summaryDataFarmPreviewTableA){
        this.summaryFilterA=[];
        this.summarySortA=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node A"),this.summaryFilterA);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node A"),this.summarySortA);
        this.clearAllSortFilterCache(sender,false);
      }else if (sender === this.summaryDataFarmPreviewTableB){
        this.summaryFilterB=[];
        this.summarySortB=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node B"),this.summaryFilterB);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node B"),this.summarySortB);
        this.clearAllSortFilterCache(sender,false);
      }else if (sender === this.summaryDataFarmPreviewTableC){
        this.summaryFilterC=[];
        this.summarySortC=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node C"),this.summaryFilterC);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node C"),this.summarySortC);
        this.clearAllSortFilterCache(sender,false);
      }else if (sender === this.summaryDataFarmPreviewTableD){
        this.summaryFilterD=[];
        this.summarySortD=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node D"),this.summaryFilterD);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node D"),this.summarySortD);
        this.clearAllSortFilterCache(sender,false);
      }else if (sender === this.summaryDataFarmPreviewTableE){
        this.summaryFilterE=[];
        this.summarySortE=[];
        this.userData.setSummaryFilter(await this.GetLocalStorageKey("Node E"),this.summaryFilterE);
        this.userData.setSummarySort(await this.GetLocalStorageKey("Node E"),this.summarySortE);
        this.clearAllSortFilterCache(sender,false);
      }else if (sender === this.summaryDataFarmPreviewTableResult){
        this.summaryFilterResult=[];
        this.summarySortResult=[];
        sender.activeFilterSet=[];
        sender.activeSortsSet=[];
        this.refreshSummaryDataFarmPreviewAsyncResult(sender,false);
      }
    }
  }
  async clearAllSortFilterCache(sender: PreviewTableComponent, isNewCache: boolean = false): Promise<void> {
    sender.activeFilterSet=[];
    sender.activeSortsSet=[];
    this.refreshSummaryDataFarmPreviewAsync(sender,isNewCache);
  }
  
  //Preview Table Delegate End
 
  isDisabled: boolean = true;
  isDisabledDW: boolean = true;
  exportcolumninfo:ExportColumnInfo[]=[];
  displayedColumnsExport: string[] = ['order','include', 'name','targetcolumn', 'dataType', 'description'];//,'index'
  DataFarmExport(){
    if(this.outputDataFarmPreviewTable.noDataTitle!="No Preview Data"){
      this.isDisabled=false;
      this.exportcolumninfo=[];
      var allDataColumns = this.dataDictService.getColumns(DataDictionaryType.all) ;
      this.outputDataFarmPreviewTable.displayedColumnInfos.forEach(ci=>{
        var datatype = allDataColumns.find(x=>x.Field == ci.name);
        if(!datatype){
          this.exportcolumninfo.push(new ExportColumnInfo(ci.index,ci.name,ci.name,"","",true));
        }else{
          this.exportcolumninfo.push(new ExportColumnInfo(ci.index,ci.name,ci.name,datatype.DataType,datatype.Description,true));
        }
      })

      this.exportOutputData.viewModelExport=this.viewModelExport;
      this.exportOutputData.exportcolumninfo=this.exportcolumninfo;
      this.exportOutputData.displayedColumnsExport=this.displayedColumnsExport;
      this.exportOutputData.bindData();
    }
    else{
      this.snackBar.open(`First Run The Transformer`, 'Dismiss', {
        duration: 5000
      });
    }
  }

  /** The pre-run check, the same for every transformer page (docs/Transformer_Error_Analysis.md,
   * "one engine"): Flask runs the Transformer error analysis on the config being edited against
   * this transformer's real input. Errors block the Run (shown in the error-analysis dialog);
   * warnings are shown and the Run continues once they are closed. If the check itself cannot
   * be made, the Run goes ahead -- the check never blocks on its own account. */
  protected async preRunCheck(): Promise<boolean> {
    let result: any;
    try {
      const ids = await this.GetCacheKeyRequest(PreviewType.transOutput);
      const transform = { ...this.transformViewModel.transform, transformConfig: this.viewModel.toTransformConfig() };
      result = await this.configService.checkTransformer({
        userId: ids.userId, orgId: ids.orgId, refineryId: ids.refineryId, nodeId: ids.nodeId, transforms: [transform] });
    } catch (err) {
      console.warn('Pre-run check unavailable; running without it', err);
      return true;
    }
    const errors: any[] = result?.errors ?? [];
    const warnings: any[] = result?.warnings ?? [];
    if (errors.length > 0) {
      this.showTransformerErrorAnalysis({
        transformer: result.transformer,
        title: `${result.transformer || 'Transformer'} cannot run yet`,
        transformType: this.viewModel?.transform?.transformType,
        failedRule: null,
        headline: errors.length === 1
          ? 'Fix this problem, then Run again.'
          : `Fix these ${errors.length} problems, then Run again.`,
        engineIssue: false,
        reportTo: null,
        findings: [...errors.map(f => ({ ...f, explainsError: true })), ...warnings],
        technical: {},
      } as TransformerErrorAnalysis);
      return false;
    }
    await this.showRuleWarnings(warnings.map(f => `${f.where}\n${f.problem}`));
    return true;
  }

  async onRunTransform() {
    if (!this.validate()) {
      return;
    }
    // Copilot drives its own runs and reports on them; the check is for the Analyst's Run.
    if (!this._isCopilotRun && !(await this.preRunCheck())) {
      return;
    }
    this.activeSortsSet=[];
    await this.runCurrentTransform();

    this.userData.setToClearActiveFilters(true);
    //await this.runCurrentTransform(undefined, undefined, !this._isCopilotRun);
    // Push actual run stats to Copilot so its next response uses real numbers
    const runStats = this.getCopilotRunStats();
    if (runStats && Object.keys(runStats).length > 0) {
      this._copilotCtx.update({ lastRunStats: runStats, currentConfig: this.getCopilotConfig() });
      this._copilotCtx.runStatsReady$.next(runStats);
    }
    //await this.runTransform();
    //Summary Refresh
    if(this.outputDataFarmPreviewTable.displayedColumnInfos){
      try {
        await this.getCacheStatus();
        this.updateNodeStatus();
        this.userData.setToClearActiveFilters(false);
        this.refreshSummaryPreviewTable();
      }catch(err) {
          this.displayError('Failed to refresh Summary Preview Table', 'Error', err);
      }
    }
    if(!this.isDisabled){
      this.DataFarmExport();
    }
  }
  selectedRows: any[] = [];
  onRowClicked(event: MouseEvent, row: any): void {
    const isSelected = this.selectedRows.includes(row);
    if (event.ctrlKey || event.metaKey) {
      // Toggle row if Ctrl (or Command on Mac) is held
      if (isSelected) {
        this.selectedRows = this.selectedRows.filter(r => r !== row);
      } else {
        this.selectedRows = [...this.selectedRows, row];
      }
    } else {
      // If Ctrl not held, select only the clicked row
      //this.selectedRows = [row];
      return;
    }
  }
 
  async getDataDictionaryType(farmtypeid:DataFarmType): Promise<number> {
    var dataDictionaryType=0;
    switch (farmtypeid) {
      case DataFarmType.activity:
        dataDictionaryType=DataDictionaryType.activity;
        break;
      case DataFarmType.subTask:
        dataDictionaryType=DataDictionaryType.subTask;
        break;
      case DataFarmType.task:
        dataDictionaryType=DataDictionaryType.task;
        break;
      case DataFarmType.cases:
        dataDictionaryType=DataDictionaryType.case;
        break;
      case DataFarmType.users:
        dataDictionaryType=DataDictionaryType.user;
        break;
      case DataFarmType.behaviors:
        dataDictionaryType=DataDictionaryType.behavior;
        break;
      case DataFarmType.master:
        dataDictionaryType=DataDictionaryType.master;
        break;  
    }
    return dataDictionaryType;
  }

  async GetCacheKeyRequest(previewType:PreviewType): Promise<GetCacheKeyRequest> {
    var request = new GetCacheKeyRequest(
        this.userData.getUserId(),
        this.userData.getOrgId(),
        this.userData.getRefineryId(),
        this.userData.getNodeId(),
        this.userData.getTransformId(),
        CacheKeyType.transformer);

    if(previewType === PreviewType.transInput) {
      // Phase 2, docs/Node_Input_Resolution_Architecture.md: resolved server-side
      // from the saved pipeline config, never from a locally-tracked
      // selectedTrans.index -- that client-side decision is what let the
      // original T1-reorder bug happen (it could go stale relative to the saved
      // config the moment a transformer was added and reordered in the same
      // action). The backend's resolve_input_location() derives this the exact
      // same way runConfigTransformercommn()'s own real input resolution does,
      // so this can never disagree with what an actual Run would use again.
      const resolved = await this.configService.resolveTransformerInput(
        this.userData.getRefineryId(), this.userData.getNodeId(), this.userData.getTransformId());
      request.cacheKeyType = resolved.cacheKeyType;
      if (resolved.cacheKeyType === CacheKeyType.node) {
        request.nodeId = resolved.nodeId;
      } else {
        request.transformId = resolved.transformId;
      }
    }else if (previewType === PreviewType.transOutputTemp) {
      request.cacheKeyType=CacheKeyType.transformerOutput;
    }else if (previewType === PreviewType.transResultTemp) {
      request.cacheKeyType=CacheKeyType.transformerResult;
    }else if (previewType === PreviewType.joinerOutput) {
      request.cacheKeyType=CacheKeyType.transformerOutput;
    }else if (previewType === PreviewType.joinerResult) {
      request.cacheKeyType=CacheKeyType.transformerResult;
    }else if (previewType === PreviewType.converterOutput) {
      request.cacheKeyType=CacheKeyType.transformer;
    }else if (previewType === PreviewType.converterResult) {
      request.cacheKeyType=CacheKeyType.transformerResult;
    }else if (previewType === PreviewType.transLookupInput) {
      const lookupFarm=(this.viewModel as any).lookupFarm;
      request.nodeId=lookupFarm;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType === PreviewType.joinerInputA) {
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===0).nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType === PreviewType.joinerInputB) {
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===1).nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType === PreviewType.joinerInputC) {
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===2).nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType === PreviewType.joinerInputD) {
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===3).nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType === PreviewType.joinerInputE) {
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===4).nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }else if (previewType >= PreviewType.joinerInputF && previewType <= PreviewType.joinerInputJ) {
      const nodeIndex = previewType - PreviewType.joinerInputF + 5;
      request.nodeId=(this.viewModel as any).fieldMap.find(x=>x.index===nodeIndex)?.nodeID;
      request.transformId="";
      request.cacheKeyType=CacheKeyType.node;
    }

    return request;
  }
  /** Rule WARNINGS found before a Run or Save -- a rule that will run, but maybe not as the
   * Analyst expects (e.g. a Combine Columns with one source field copies it unchanged). Unlike
   * a validation error they never block: shown together, one rule per paragraph ("Rule N:
   * Name" first, TransformRuleViewModel.ruleMessage), and the Run / Save continues when the
   * Analyst closes the dialog. Every transformer page uses this for warnings. */
  protected showRuleWarnings(warnings: string[]): Promise<void> {
    if (!warnings || warnings.length === 0) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve =>
      this.displayWarning(warnings.join('\n\n'), 'Warning', undefined, () => resolve()));
  }

  /** A failed Transformer run, explained: where the problem is, what is wrong and how to fix
   * it, from the uncompiled engine's error analysis (docs/Transformer_Error_Analysis.md). */
  protected showTransformerErrorAnalysis(analysis: TransformerErrorAnalysis): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '720px';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.autoFocus = false;
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'warning-dialog';
    dialogConfig.data = analysis;
    this.matDialog.open(TransformerErrorDialogComponent, dialogConfig);
  }

  async runCurrentTransform(filters?: ColumnFilterInfo[], outputFarmFilters?: ColumnFilterInfo[],showDialog: boolean = true): Promise<void> {
    const pipelineVm = new TransformPipelineViewModel();
    pipelineVm.items=[];
    const groupedItem = new GroupedTransformPipelineItem();
    groupedItem.groupName="";
    groupedItem.items=[];
    groupedItem.items.push(new TransformPipelineItem(this.userData.getSelectedTransform().name, groupedItem.groupName, 'Pending', null, true,"",""));
    pipelineVm.items.push(groupedItem);
    const busyIndicator = showDialog ? this.showPipelineDialog(pipelineVm,'Executing transforms...') : null;
    const busyIndicatorComp = busyIndicator?.componentInstance;
    try {
      if(!this.userData.getApplyFilterBtnClicked()) {
        this.userData.setToClearActiveFilters(true);
      }
      var currenttransformer = this.viewModel.toTransformConfig();
      var currenttransformer_sampleStart = currenttransformer.sampleStart + 1 ;
      var currenttransformer_sampleEnd = currenttransformer.sampleEnd + 1 ;
      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
        if (currenttransformer_sampleStart >= currenttransformer_sampleEnd) {
            this.displayWarning(
              'Sample Start was greater than to Sample End, so it was auto-reset to 1.',
              'Warning',
              undefined,
              () => {
                this.viewModel.parameters.sampleStart = 1;
              }
            );
            return;
        }
        if(currenttransformer_sampleStart > this.totalInputRows || currenttransformer_sampleEnd > this.totalInputRows )
        {
          this.displayWarning(
            'Sample Range exceeded the Input Farm size and was auto-corrected to the maximum limit.',
            'Warning',
            undefined,  // no exception to pass
            () => {
              if (currenttransformer_sampleStart > this.totalInputRows) {
              this.viewModel.parameters.sampleStart = 1;
              }
              if (currenttransformer_sampleEnd > this.totalInputRows) {
                this.viewModel.parameters.sampleEnd = this.totalInputRows;
              }
            }
          );
          return;
        }
      }
      
      const previewOptions = new PreviewDataOptions(this.previewTable.pageIndex, this.previewTable.rowsPerPage,this.activeResultSortsSet);
      previewOptions.filters = filters?.map(f => {
        const filterInfo = new ColumnFilter();
        filterInfo.field = f.columnName;
        filterInfo.values = f.values;
        return filterInfo;
      });

      const farmDataPreviewOptions = new PreviewDataOptions(
        this.outputDataFarmPreviewTable.pageIndex,
        this.outputDataFarmPreviewTable.rowsPerPage,this.activeSortsSet);
      farmDataPreviewOptions.filters = outputFarmFilters?.map(f => {
        const filterInfo = new ColumnFilter();
        filterInfo.field = f.columnName;
        filterInfo.values = f.values;
        return filterInfo;
      });

      var cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutput);
      var cachekeyRequestInput=await this.GetCacheKeyRequest(PreviewType.transInput);
      const parentNode = this.userData.getNodeParentDataLine()[0].source;
      const request = new RunCurrentConfigRequest();
      request.previewOptions = previewOptions;
      request.farmDataOutPreviewOptions = farmDataPreviewOptions;
      
      request.userId = cachekeyRequest.userId;
      request.orgId = cachekeyRequest.orgId;
      request.refineryId = cachekeyRequest.refineryId;
      request.nodeId = cachekeyRequest.nodeId;
      //For input data
      request.inputCacheKeyType = cachekeyRequestInput.cacheKeyType;
      if(cachekeyRequestInput.cacheKeyType==CacheKeyType.node){
        request.inputNodeId = cachekeyRequestInput.nodeId;
        request.inputTransformId = "";
      }else{
        request.inputNodeId = parentNode;
        request.inputTransformId = cachekeyRequestInput.transformId;
      }

      request.transforms = [];
      const transformConfig = this.viewModel.toTransformConfig();
      this.transformViewModel.transform.transformConfig = transformConfig;
      request.transforms.push(this.transformViewModel.transform);
      //request.transforms.push(DataTransformerConfig.deserializeTransform(this.transformViewModel.transform));

      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
        if(this.transformViewModel.transform.transformConfig.rules){
          var rulesData = this.transformViewModel.transform.transformConfig.rules;
          this.resultTableColumns = [...this.viewModel.sourceColumns] ;

          // Define the new column object with the next index for Input string to make yellow and white - DT-1288
          const newColumn = {
            "value": 'Input string',
            "text": 'Input string',
            "isSelected": false
          };
          this.resultTableColumns.push(newColumn);

          const distinctTargetFields = (data: any[]): string[] => data.map(item => item.targetField).filter((value, index, self) => self.indexOf(value) === index);
          
          const distinctFields = distinctTargetFields(rulesData);

          distinctFields.forEach(field => {
              const newColumn = {
                  "value": field,
                  "text": field,
                  "isSelected": false
              };
              this.resultTableColumns.push(newColumn);
          });   
        
        }
      }

      const item = pipelineVm.items[0].items[0];
      item.isRunning = true;
      item.statusDisplayText = 'Executing...';
      this._dtrRunning = true;

      //const response: PreviewDataImportResponse = await this.configService.runCurrentConfig(request);
      let response: PreviewDataImportResponse =null;
      try {
        const requestUrl = await this.configService.runCurrentConfigUrl();
        await this.configService.streamPost<RunCurrentConfigRequest, RunrefineryResponse>(
          requestUrl,
          request,
          (msg) => {
            console.log("Progress message:", msg);
          }
        ).then(async result => {
          if (result){
            if(result['status'] === 'finished'){
               response=result['data'];
               // Run-time warnings from the engine (e.g. every row deleted): shown, never blocking.
               const runWarnings: string[] = (result as any)?.warnings ?? [];
               if (runWarnings.length > 0) {
                 this.showRuleWarnings(runWarnings);
               }
               if (response != null) {
                  if (response.transformerTime) {
                    // A cache hit means the real engine never executed for THIS request —
                    // but as of 2026-10-01 (the Architect, explicit sign-off) the server
                    // now persists and restores the LAST real Run Time this exact config
                    // actually produced, rather than a live measurement of the cache read
                    // itself. So this is no longer "an indistinguishable raw number" to
                    // hide behind a blanket "Cached" label — it's the real answer to "how
                    // long did this config take to run," same as a fresh run, just not
                    // measured just now. Only fall back to "Cached" when the server
                    // explicitly has nothing restorable (an older cache entry written
                    // before this field existed) — it sends "NA" for exactly that case,
                    // same convention already used for compiledTransformerTime.
                    // Never reformat "NA" through _formatDtrRunTime, which expects a real
                    // duration and would throw on it -- same guard already used below for
                    // compiledTransformerTime.
                    this.lastDtrRunTime = (response.transformerTime === 'NA')
                      ? 'Cached'
                      : this._formatDtrRunTime(response.transformerTime);
                    // compiledTransformerTime is either a real duration string (same
                    // format as transformerTime) or the literal "NA" — never reformat
                    // "NA" (or a missing value, from an older response shape) through
                    // _formatDtrRunTime, which expects a real duration and would throw
                    // on undefined.
                    this.lastCompiledRunTime =
                      (!response.compiledTransformerTime || response.compiledTransformerTime === 'NA')
                        ? 'NA'
                        : this._formatDtrRunTime(response.compiledTransformerTime);
                    // Thread count persists/restores on a cache hit the same way the run
                    // time itself does now -- only null when there's genuinely nothing
                    // restorable (the "Cached" text case above).
                    this.lastDtrThreadsUsed = (response.transformerTime === 'NA') ? null : (response.transformerThreadsUsed ?? null);
                    this.lastCompiledThreadsUsed = response.compiledThreadsUsed ?? null;
                    this._upsertDtrRunStat();
                    this._copilotCtx.dtrRunMs$.next(
                      (response.transformerTime === 'NA') ? -1 : this._parseDtrRunTimeMs(response.transformerTime)
                    );
                  }
                  this.previewTable.bindData(response.activeData, 'No Results');
                  this.outputDataFarmPreviewTable.bindData(response.currentData, 'No Farm Data');
                  this.outputcolumncount=response.currentData.columns.length;
                  const item = pipelineVm.items[0].items[0];
                  item.isRunning = true;
                  item.statusDisplayText = 'Completed!';
                  item.cacheUpdatedStatus =response.cacheUpdatedStatus;
                  item.transformerTime =response.transformerTime;
                  this.LoadSummaryPersistance(this.summaryDataFarmPreviewTableOutput, true);
                  if (!this._isCopilotPreviewRun && !this._skipResultSummaryRefresh) {
                    await this.refreshSummaryDataFarmPreviewAsyncResult(this.summaryDataFarmPreviewTableResult,true);
                  }
                  this.snackBar.open(`Transform results have been updated!`, 'Dismiss', {
                      duration: 3000
                    });
                  
                  if(this.viewModel.transform.transformType==TransformType.farmConverter){
                    this.viewModel.onTransformRunCompleted(response as FarmConverterTransformPreviewDataResponse);
                  }else{
                    this.viewModel.onTransformRunCompleted(response as RowExtractorPreviewDataResponse);
                  }
                }

                if(this.outputDataFarmPreviewTable.totalRows < 1 && this.viewModel.toTransformConfig().runAll == true){
                  this.displayWarning('This configuration produces an empty (0 rows) output data farm.  This will causes failures in downstream processing.  Please correct, test and save.', 'Warning');
                }                
            }else{
              const item = pipelineVm.items[0].items[0];
              item.isRunning = true;
              item.statusDisplayText = `Failed!`;
              if ((result as any)?.analysis) {
                // The Transformer error analysis: which rule, what is wrong, how to fix it
                // (docs/Transformer_Error_Analysis.md).
                this.showTransformerErrorAnalysis((result as any).analysis);
              } else {
                this.displayError(result['message'].toString(), 'Failed to run transformer',result['message'].toString());
              }
            }
          }
        });
      } catch(err) {
          this.displayError('Failed to run the transformer', 'Error', err);
      }
      
    } catch (err) {
      const item = pipelineVm.items[0].items[0];
      item.isRunning = true;
      item.statusDisplayText = `Failed!`;
      this.displayError('Failed to run transform', 'Error', err);
    } finally {
      setTimeout(() => {
        busyIndicatorComp.close();
      }, 1000); // 1 seconds delay
      this.viewModel.isTransformRunning = false;
      this.userData.setApplyFilterBtnClicked(false);
      this._dtrRunning = false;
      this.onCopilotDtrComplete();
    }
  }

  /** Called by runCurrentTransform's finally when a Copilot-triggered DTR run finishes.
   *  Override in subclasses to serialize re-runs and suppress premature navigation signals. */
  protected onCopilotDtrComplete(): void {
    this._dtrRunComplete$.next();
  }

  async runTransformOld(
    filters?: ColumnFilterInfo[],
    outputFarmFilters?: ColumnFilterInfo[],
    showDialog: boolean = true,
    showCompletionMessage: boolean = true): Promise<void> {
    if (!this.validate()) {
      return;
    }
    if(!this.userData.getApplyFilterBtnClicked()) {
        this.userData.setToClearActiveFilters(true);
    }

    this.viewModel.isTransformRunning = true;

    const minDisplayEndTime = new Date(Date.now() + this.minDialogDisplayTimeMs);
    const pipelineVm = new TransformPipelineViewModel();
    const busyIndicator = showDialog ? this.showPipelineDialog(pipelineVm, 'Executing transforms...') : null;
    const busyIndicatorComp = busyIndicator?.componentInstance;  // hold on to reference or else will be null after it is closed

    try {
      // Keep track of if sortInfo changed
      const sortInfoJson = JSON.stringify(this.viewModel.sortInfo);
      const didSortChange = sortInfoJson !== this.viewModel.lastSortInfoJson;
      this.viewModel.lastSortInfoJson = sortInfoJson;
      var CurrentNode = this.userData.getSelectedNodeDetails();
      // // Add NodeID to this.dataTransformConfigViewModel.transforms

      // this.dataTransformConfigViewModel.transforms.forEach(subArray => {
      //   // Add NodeID property to each sub-transform
      //   subArray.transform.nodeID = CurrentNode.id;
      // });
      // Refresh input farm async to keep sort order in sync with output
      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
        if (didSortChange) {
          this.refreshInputDataFarmPreviewAsync(this.inputDataFarmPreviewTable);
        }
      }

      var currenttransformer = this.viewModel.toTransformConfig();
      var currenttransformer_sampleStart = currenttransformer.sampleStart + 1 ;
      var currenttransformer_sampleEnd = currenttransformer.sampleEnd + 1 ;
      if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
     if (currenttransformer_sampleStart >= currenttransformer_sampleEnd) {
            this.displayWarning(
              'Sample Start was greater than to Sample End, so it was auto-reset to 1.',
              'Warning',
              undefined,
              () => {
                this.viewModel.parameters.sampleStart = 1;
              }
            );
            return;
          }
        if(currenttransformer_sampleStart > this.totalInputRows || currenttransformer_sampleEnd > this.totalInputRows )
        {
          // this.viewModel.parameters.sampleStart = 1;
          // this.viewModel.parameters.sampleEnd = this.inputDataFarmPreviewTable.totalRows - 1;
          this.displayWarning(
            'Sample Range exceeded the Input Farm size and was auto-corrected to the maximum limit.',
            'Warning',
            undefined,  // no exception to pass
            () => {
              //this.viewModel.parameters.sampleEnd = this.totalInputRows;
              if (currenttransformer_sampleStart > this.totalInputRows) {
              this.viewModel.parameters.sampleStart = 1;
              }
              if (currenttransformer_sampleEnd > this.totalInputRows) {
                this.viewModel.parameters.sampleEnd = this.totalInputRows;
              }
            }
          );
          return;
        }
      }
      // return;

      // We need to build the transform pipeline
      // and execute each transform starting from the top
      var nodeID=this.userData.getSelectedNodeDetails().id;
      var refineryID=this.userData.getSelectedNodeDetails().refineryConfigGuid  ;
      const pipeline = this.transformPipelineService.buildPipeline(
        this.transformViewModel.guid,
        this.dataTransformConfigViewModel.transforms.filter(x=>x.nodeID === nodeID));
      // Build view Model of Transform Pipeline
      pipelineVm.items = pipeline.groupedTransforms.map(g => {
        const group = new GroupedTransformPipelineItem();
        group.groupName = g.groupName;
        group.items = g.transforms.map(t => {
          return new TransformPipelineItem(t.name, g.groupName, 'Pending', t, t.guid === this.transformViewModel.guid,"","");
        });
        return group;
      }
      );

      const dataConnectors = this.viewModel.buildDataConnectorMappings();
      // Get the active input ingest data farm (data connector)
      // and ensure every transform runs with the correct one

      const activeDataConnector = dataConnectors.find(d => d.guid === this.dataTransformConfigViewModel.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.dataTransformConfigViewModel.selectedIngestFarmGuid}`);
      }

      let didFail = false;
      let transformerRunType = (this.userData.getSelectedNodeDetails().nodeboxtypename == "Joiner" || this.userData.getSelectedNodeDetails().nodeboxtypename == "Converter") ? "transformer_prev" : "transformer_run"
      const activeTransformGuid = this.userData.getSelectedTransform().guid
      // Execute the pipeline
      await this.transformPipelineService.executePipelineAsyncOld(
        refineryID,
        this.parentNodes, 
        this.AttrNodes, 
        pipeline,
        dataConnectors,
        activeDataConnector.guid,
        transformerRunType,
        activeTransformGuid,
         (transform, groupName) => {
          pipelineVm.currentGroup = groupName;

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transform)[0];

          // Update the active source data farm, even though it's not the first one
          // this should make the transform dirty so it runs again
          // as the root input farm has changed.
          // TODO: Perhaps we us an isDirty or invalidated property instead?
          item.isRunning = true;
          item.statusDisplayText = 'Executing...';
        },
        (request) => { if (this.compiledConfig) { request.compiledConfig = this.compiledConfig; } },
        (transformerViewModel, groupName, success, response, err) => {
          if(!success){
            this.logError("Failed to run transform",err);
          }
          didFail = !success && err != null;

          if (busyIndicatorComp) {
            busyIndicatorComp.hasError = didFail;
          }

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transformerViewModel)[0];
          item.isRunning = false;
          item.statusDisplayText = success ? 'Completed!' : `Failed!`;
          // response=null on failure (see the other fixed call sites in this file for the
          // full explanation) -- guarded the same way here.
          if (response) {
            item.cacheUpdatedStatus = response.cacheUpdatedStatus;
            item.transformerTime = response.transformerTime;
          }

          if (transformerViewModel.guid === this.transformViewModel.guid && response != null) {
            this.previewTable.bindData(response.activeData, 'No Results');
            this.outputDataFarmPreviewTable.bindData(response.currentData, 'No Farm Data');
            this.outputcolumncount=response.currentData.columns.length;

            if (showCompletionMessage) {
              this.snackBar.open(`Transform results have been updated!`, 'Dismiss', {
                duration: 3000
              });
            }

            if(this.viewModel.transform.transformType==TransformType.farmConverter){
              this.viewModel.onTransformRunCompleted(response as FarmConverterTransformPreviewDataResponse);
            }else{
              this.viewModel.onTransformRunCompleted(response as RowExtractorPreviewDataResponse);
            }
          }
        }, transformerViewModel => {

          // Build the current transform request so we can render it's results properly
          if (transformerViewModel.guid === this.transformViewModel.guid) {
            const previewOptions = new PreviewDataOptions(this.previewTable.pageIndex, this.previewTable.rowsPerPage,this.activeResultSortsSet);
            previewOptions.filters = filters?.map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });

              this.activeSortsSet = this.activeSortsSet.filter(
               item => !item.sortField.startsWith("smry_count")
            );
            
            const farmDataPreviewOptions = new PreviewDataOptions(
              this.outputDataFarmPreviewTable.pageIndex,
              this.outputDataFarmPreviewTable.rowsPerPage,this.activeSortsSet);
            farmDataPreviewOptions.filters = outputFarmFilters?.map(f => {
              const filterInfo = new ColumnFilter();
              filterInfo.field = f.columnName;
              filterInfo.values = f.values;
              return filterInfo;
            });

            const request = new RunConfigRequest();
            request.activeIngestFarmId = activeDataConnector.guid;
            request.requireResults = true;
            request.previewOptions = previewOptions;
            request.farmDataOutPreviewOptions = farmDataPreviewOptions;
            request.dataSourceType = DataSourceType.timeStampedData;
            request.activeTransformId = activeTransformGuid;
            request.transformerRunType = transformerRunType;
            request.userId = this.userData.getUserId();
            request.orgId = this.userData.getOrgId();
            request.refineryID = this.userData.getRefineryId();
            request.nodeId = this.userData.getNodeId();
            // Only send up the data connectors if we are the first transform
            // as this one reads from the data connector for input
            if (pipeline.transforms.indexOf(transformerViewModel) === 0) {
              request.dataConnectorMappings = dataConnectors;
            }

            request.transforms = [];
            request.transforms.push(transformerViewModel.transform);

            const transformConfig = this.viewModel.toTransformConfig();
            transformerViewModel.transform.transformConfig = transformConfig;
            if(this.viewModel.transform.transformType!=TransformType.farmJoiner){
              if(transformerViewModel.transform.transformConfig.rules){
                var rulesData = transformerViewModel.transform.transformConfig.rules;
                this.resultTableColumns = [...this.viewModel.sourceColumns] ;
                // Define the new column object with the next index for Input string to make yellow and white - DT-1288
                const newColumn = {
                  "value": 'Input string',
                  "text": 'Input string',
                  "isSelected": false
                };
                this.resultTableColumns.push(newColumn);

                const distinctTargetFields = (data: any[]): string[] => data.map(item => item.targetField).filter((value, index, self) => self.indexOf(value) === index);
                
                const distinctFields = distinctTargetFields(rulesData);

                distinctFields.forEach(field => {
                    const newColumn = {
                        "value": field,
                        "text": field,
                        "isSelected": false
                    };
                    this.resultTableColumns.push(newColumn);
                });   
              
              }
            }
            
            return request;
          } else {
            // Ensure we set Transient runAll property to true for all pre transforms so full set of data is processed 
            transformerViewModel.transform.setRunAll(true);

            return null;
          }
      });

        if(this.outputDataFarmPreviewTable.totalRows < 1 && this.viewModel.toTransformConfig().runAll == true){
          this.displayWarning('This configuration produces an empty (0 rows) output data farm.  This will causes failures in downstream processing.  Please correct, test and save.', 'Warning');
        }

        //Summary Refresh
        // if(this.outputDataFarmPreviewTable.displayedColumnInfos){
        //   this.refreshSummaryPreviewTable();
        // }
    } catch (err) {
      this.displayError('Failed to run transform', 'Error', err);
    } finally {
      const diff = Date.now() - minDisplayEndTime.getTime();
      if (showDialog && diff < 0) {
        await this.delay(Math.abs((diff)));
      }

      if (busyIndicatorComp) {
        if (busyIndicatorComp.hasError) {
          await this.delay(4000);
        }

        busyIndicatorComp.close();
      }

      this.viewModel.isTransformRunning = false;
      this.userData.setApplyFilterBtnClicked(false);
    }
  }
  async updateNodeStatus(): Promise<void> {
    try {
      const refineryDetails = this.userData.getSelectedRefineryDetails();
      const nodeId = this.userData.getNodeId();
      const node = refineryDetails.nodes.find(n => n.id === nodeId);
      const currentStatus = node.data.nodeStatus;
      let newNodeStatus = NodeRunStatus.Idle;
      if (currentStatus === NodeRunStatus.Idle || currentStatus === NodeRunStatus.NotRun || currentStatus === NodeRunStatus.Completed) {
        var request=new ResetNodeStatusRequest(refineryDetails.guid,nodeId);
        const cacheStatusResponse = await this.configService.getTransformersNodeStatus(request);
        if(cacheStatusResponse.transformerCacheStatus.length >0){
          const cachedIds = new Set(cacheStatusResponse.transformerCacheStatus);
          const transforms = this.dataTransformConfigViewModel.transforms.filter(t => t.nodeID === nodeId);
          
          const allCached = transforms.every(t =>
            cachedIds.has(t.guid)
          );
          newNodeStatus = allCached ? NodeRunStatus.Completed : NodeRunStatus.NotRun;
          // Persist only if status changed
          if (currentStatus !== newNodeStatus) {
            node.data.nodeStatus = newNodeStatus;
            // This save is a side effect of merely OPENING/viewing a node's
            // transform-detail panel (see the caller chain from
            // ngOnInit -> onRouteActivated -> downloadInputDataAsync), not
            // an explicit Analyst action — it must never fire from a
            // Read-Only tab. The backend would reject it anyway (no Edit
            // lock held), but skipping it here avoids a needless 403 on
            // every single Read-Only node open.
            if (this.refineryPresence.mode === 'edit') {
              await this.configService.UpsertRefineryConfig(refineryDetails);
            }
          }
        }
      }
    }catch (err) {
      this.displayError('Failed to Update Node Status', 'Error', err);
    } finally {
      //sender.isGettingData.next(false);
    }
  }

  showTextEditorDialogOption(
    title: string,
    label: string,
    placeholder: string,
    fieldType: string,
    isDuplicateRemoved: boolean,
    canAddNewAutocomplete: boolean,
    options: string[],
    dialogWidth: string,
    existingItems: ColoredTextItem[],
    onClosed: (items: ColoredTextItem[],eventname:string) => any,showRefresh: boolean=false,refreshTooltip: string="Refresh" ): void {

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {items: existingItems, title, label,placeholder,fieldType,isDuplicateRemoved,canAddNewAutocomplete,options,showRefresh,refreshTooltip,onClosed};
    dialogConfig.width = dialogWidth;
  
    const dialogRef = this.matDialog.open(ContainsTextDialogOptionComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      onClosed(result.items,result.eventname);       
    });
  }

  showFindReplaceEditorDialogOption(
    title: string,
    labelFind: string,
    labelReplace: string,
    placeholderFind: string,
    placeholderReplace: string,
    fieldType: string,
    isDuplicateRemoved: boolean,
    canAddNewAutocomplete: boolean,
    optionsFind: OptionItemDataType[],
    optionsReplace: OptionItemDataType[],
    validateboth: boolean,
    isDataTypeCheck: boolean,
    dialogWidth: string,
    existingItems: ColoredTextFindReplaceItem[],
    onClosed: (items: ColoredTextFindReplaceItem[],eventname:string) => any): void {

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {items: existingItems,title,labelFind,labelReplace,placeholderFind,placeholderReplace,fieldType,isDuplicateRemoved,canAddNewAutocomplete,optionsFind,optionsReplace,validateboth,isDataTypeCheck};
    dialogConfig.width = dialogWidth;
  
    const dialogRef = this.matDialog.open(FindReplaceChooserMapDialogOptionComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      onClosed(result.items,result.eventname);
    });
  }

  async GetLocalStorageKey(type:string): Promise<string> {
    return this.userData.getRefineryId()+"_"+this.userData.getNodeId()+"_"+this.userData.getTransformId()+"_"+type;
  }

  async LoadSummaryPersistance(sender: PreviewTableComponent, isNewCache: boolean = false): Promise<void> {
    try{
      if(sender === this.summaryDataFarmPreviewTableInput){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Input"))){
          this.selectValuesInput=this.userData.getSelectValues(await this.GetLocalStorageKey("Input"));
          this.selectEmptyColumnsInput=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Input"));
          this.selectFilterValuesInput=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Input"));
          this.summaryFilterInput=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Input"));
          this.summarySortInput=this.userData.getSummarySort(await this.GetLocalStorageKey("Input"));
          if(!this.selectEmptyColumnsInput){
            this.selectEmptyColumnsInput=[];
          }
          if(!this.selectFilterValuesInput){
            this.selectFilterValuesInput=[];
          }
          if(!this.summaryFilterInput){
            this.summaryFilterInput=[];
          }else{
            this.summaryDataFarmPreviewTableInput.activeFilterSet=this.summaryFilterInput ?? [];
            this.summaryDataFarmPreviewTableInput.activeColumnFilters=(this.summaryFilterInput ?? []).map(x => x.columnName);
          }
          if(!this.summarySortInput){
            this.summarySortInput=[];
          }else{
            this.summaryDataFarmPreviewTableInput.activeSortsSet=this.summarySortInput ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesInput;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableInput,isNewCache);
        }
      }else if(sender === this.summaryDataFarmPreviewTableOutput) {
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Output"))){
          if(!this.outputDataFarmPreviewTable.displayedColumnInfos){
            if(this.viewModel.transform.transformType==TransformType.farmJoiner){
              await this.getDataFarmPreviewAsync(this.outputDataFarmPreviewTable,PreviewType.joinerOutput);
            }else{
              await this.getDataFarmPreviewAsync(this.outputDataFarmPreviewTable,PreviewType.transOutputTemp);
            }
          }
          if(this.outputDataFarmPreviewTable.displayedColumnInfos){
            this.selectValuesOutput=this.userData.getSelectValues(await this.GetLocalStorageKey("Output"));
            this.selectEmptyColumnsOutput=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Output"));
            this.selectFilterValuesOutput=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Output"));
            this.summaryFilterOutput=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Output"));
            this.summarySortOutput=this.userData.getSummarySort(await this.GetLocalStorageKey("Output"));
            if(!this.selectEmptyColumnsOutput){
              this.selectEmptyColumnsOutput=[];
            }
            if(!this.selectFilterValuesOutput){
              this.selectFilterValuesOutput=[];
            }
            if(!this.summaryFilterOutput){
              this.summaryFilterOutput=[];
            }else{
              this.summaryDataFarmPreviewTableOutput.activeFilterSet=this.summaryFilterOutput ?? [];    
              this.summaryDataFarmPreviewTableOutput.activeColumnFilters=(this.summaryFilterOutput ?? []).map(x => x.columnName);       
            }
            if(!this.summarySortOutput){
              this.summarySortOutput=[];
            }else{
              this.summaryDataFarmPreviewTableOutput.activeSortsSet=this.summarySortOutput ?? [];
            }
            
            this.selectFilterValues=this.selectFilterValuesOutput;
            await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableOutput,isNewCache);
          }
        }
      }else if(sender === this.summaryDataFarmPreviewTableLookup) {
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Lookup"))){
          this.selectValuesLookup=this.userData.getSelectValues(await this.GetLocalStorageKey("Lookup"));
          this.selectEmptyColumnsLookup=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Lookup"));
          this.selectFilterValuesLookup=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Lookup"));
          this.summaryFilterLookup=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Lookup"));
          this.summarySortLookup=this.userData.getSummarySort(await this.GetLocalStorageKey("Lookup"));
          if(!this.selectEmptyColumnsLookup){
            this.selectEmptyColumnsLookup=[];
          }
          if(!this.selectFilterValuesLookup){
            this.selectFilterValuesLookup=[];
          }
          if(!this.summaryFilterLookup){
            this.summaryFilterLookup=[];
          }else{
            this.summaryDataFarmPreviewTableLookup.activeFilterSet=this.summaryFilterLookup ?? [];
            this.summaryDataFarmPreviewTableLookup.activeColumnFilters=(this.summaryFilterLookup ?? []).map(x => x.columnName);
          }
          if(!this.summarySortLookup){
            this.summarySortLookup=[];
          }else{
            this.summaryDataFarmPreviewTableLookup.activeSortsSet=this.summarySortLookup ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesLookup;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableLookup,isNewCache);
        } 
      }else if(sender === this.summaryDataFarmPreviewTableA){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Node A"))){
          this.selectValuesA=this.userData.getSelectValues(await this.GetLocalStorageKey("Node A"));
          this.selectEmptyColumnsA=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Node A"));
          this.selectFilterValuesA=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Node A"));
          this.summaryFilterA=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Node A"));
          this.summarySortA=this.userData.getSummarySort(await this.GetLocalStorageKey("Node A"));
          if(!this.selectEmptyColumnsA){
            this.selectEmptyColumnsA=[];
          }
          if(!this.selectFilterValuesA){
            this.selectFilterValuesA=[];
          }
          if(!this.summaryFilterA){
            this.summaryFilterA=[];
          }else{
            this.summaryDataFarmPreviewTableA.activeFilterSet=this.summaryFilterA ?? [];
            this.summaryDataFarmPreviewTableA.activeColumnFilters=(this.summaryFilterA ?? []).map(x => x.columnName);
          }
          if(!this.summarySortA){
            this.summarySortA=[];
          }else{
            this.summaryDataFarmPreviewTableA.activeSortsSet=this.summarySortA ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesA;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableA,isNewCache);
        }
      }else if (sender === this.summaryDataFarmPreviewTableB){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Node B"))){
          this.selectValuesB=this.userData.getSelectValues(await this.GetLocalStorageKey("Node B"));
          this.selectEmptyColumnsB=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Node B"));
          this.selectFilterValuesB=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Node B"));
          this.summaryFilterB=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Node B"));
          this.summarySortB=this.userData.getSummarySort(await this.GetLocalStorageKey("Node B"));
          if(!this.selectEmptyColumnsB){
            this.selectEmptyColumnsB=[];
          }
          if(!this.selectFilterValuesB){
            this.selectFilterValuesB=[];
          }
          if(!this.summaryFilterB){
            this.summaryFilterB=[];
          }else{
            this.summaryDataFarmPreviewTableB.activeFilterSet=this.summaryFilterB ?? [];
            this.summaryDataFarmPreviewTableB.activeColumnFilters=(this.summaryFilterB ?? []).map(x => x.columnName);
          }
          if(!this.summarySortB){
            this.summarySortB=[];
          }else{
            this.summaryDataFarmPreviewTableB.activeSortsSet=this.summarySortB ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesB;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableB,isNewCache);
        }
      }else if (sender === this.summaryDataFarmPreviewTableC){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Node C"))){
          this.selectValuesC=this.userData.getSelectValues(await this.GetLocalStorageKey("Node C"));
          this.selectEmptyColumnsC=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Node C"));
          this.selectFilterValuesC=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Node C"));
          this.summaryFilterC=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Node C"));
          this.summarySortC=this.userData.getSummarySort(await this.GetLocalStorageKey("Node C"));
          if(!this.selectEmptyColumnsC){
            this.selectEmptyColumnsC=[];
          }
          if(!this.selectFilterValuesC){
            this.selectFilterValuesC=[];
          }
          if(!this.summaryFilterC){
            this.summaryFilterC=[];
          }else{
            this.summaryDataFarmPreviewTableC.activeFilterSet=this.summaryFilterC ?? [];
            this.summaryDataFarmPreviewTableC.activeColumnFilters=(this.summaryFilterC ?? []).map(x => x.columnName);
          }
          if(!this.summarySortC){
            this.summarySortC=[];
          }else{
            this.summaryDataFarmPreviewTableC.activeSortsSet=this.summarySortC ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesC;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableC,isNewCache);
        }
      }else if (sender === this.summaryDataFarmPreviewTableD){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Node D"))){
          this.selectValuesD=this.userData.getSelectValues(await this.GetLocalStorageKey("Node D"));
          this.selectEmptyColumnsD=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Node D"));
          this.selectFilterValuesD=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Node D"));
          this.summaryFilterD=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Node D"));
          this.summarySortD=this.userData.getSummarySort(await this.GetLocalStorageKey("Node D"));
          if(!this.selectEmptyColumnsD){
            this.selectEmptyColumnsD=[];
          }
          if(!this.selectFilterValuesD){
            this.selectFilterValuesD=[];
          }
          if(!this.summaryFilterD){
            this.summaryFilterD=[];
          }else{
            this.summaryDataFarmPreviewTableD.activeFilterSet=this.summaryFilterD ?? [];
            this.summaryDataFarmPreviewTableD.activeColumnFilters=(this.summaryFilterD ?? []).map(x => x.columnName);
          }
          if(!this.summarySortD){
            this.summarySortD=[];
          }else{
            this.summaryDataFarmPreviewTableD.activeSortsSet=this.summarySortD ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesD;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableD,isNewCache);
        }
      }else if (sender === this.summaryDataFarmPreviewTableE){
        if(this.userData.getSelectValues(await this.GetLocalStorageKey("Node E"))){
          this.selectValuesE=this.userData.getSelectValues(await this.GetLocalStorageKey("Node E"));
          this.selectEmptyColumnsE=this.userData.getSelectEmptyColumns(await this.GetLocalStorageKey("Node E"));
          this.selectFilterValuesE=this.userData.getSelectFilterValues(await this.GetLocalStorageKey("Node E"));
          this.summaryFilterE=this.userData.getSummaryFilter(await this.GetLocalStorageKey("Node E"));
          this.summarySortE=this.userData.getSummarySort(await this.GetLocalStorageKey("Node E"));
          if(!this.selectEmptyColumnsE){
            this.selectEmptyColumnsE=[];
          }
          if(!this.selectFilterValuesE){
            this.selectFilterValuesE=[];
          }
          if(!this.summaryFilterE){
            this.summaryFilterE=[];
          }else{
            this.summaryDataFarmPreviewTableE.activeFilterSet=this.summaryFilterE ?? [];
            this.summaryDataFarmPreviewTableE.activeColumnFilters=(this.summaryFilterE ?? []).map(x => x.columnName);
          }
          if(!this.summarySortE){
            this.summarySortE=[];
          }else{
            this.summaryDataFarmPreviewTableE.activeSortsSet=this.summarySortE ?? [];
          }
          
          this.selectFilterValues=this.selectFilterValuesE;
          await this.refreshSummaryDataFarmPreviewAsync(this.summaryDataFarmPreviewTableE,isNewCache);
        }
      }
    }catch(err){
      console.log("Failed to load summary persistance",err);  
    }
  }
  async getDataFarmPreviewAsync(sender: PreviewTableComponent,previewType:PreviewType): Promise<void> {
    sender.isGettingData.next(true);
    try {
      const filters = (sender.activeFilterSet ?? []).map(f => {
        const filterInfo = new ColumnFilter();
        filterInfo.field = f.columnName;
        filterInfo.values = f.values;
        return filterInfo;
      });
      const sortInfo = (sender.activeSortsSet ?? []);
      const options = new PreviewDataOptions(sender.pageIndex, sender.rowsPerPage, sortInfo, filters);
      const cachekeyRequest=await this.GetCacheKeyRequest(previewType);
      const request = new GetDataFarmPreviewRequest(
        cachekeyRequest.userId,
        cachekeyRequest.orgId,
        cachekeyRequest.refineryId,
        cachekeyRequest.nodeId,
        cachekeyRequest.transformId,
        cachekeyRequest.cacheKeyType,
        options);
      const response = await this.configService.getDataFarmPreviewAsync(request);

      sender.bindData(response.data, 'No input preview data');
      if (sender == this.inputDataFarmPreviewTable) {
        this.inputcolumncount=response.data.columns.length;
      } else if (sender == this.outputDataFarmPreviewTable){
        this.outputcolumncount=response.data.columns.length;
      }
    } catch (err) {
      //this.displayError('Failed to get data farm preview', 'Error', err);
      console.log('Failed to get data farm preview', err)
    } finally {
      sender.isGettingData.next(false);
    }
  }
  async getCacheStatus(): Promise<void> {
    try {
      const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutput);
      const request = new CacheStatusRequest(
        cachekeyRequest.userId,
        cachekeyRequest.orgId,
        cachekeyRequest.refineryId,
        cachekeyRequest.nodeId,
        cachekeyRequest.transformId,
        cachekeyRequest.cacheKeyType,
        this.viewModel.toTransformConfig(),
        this.viewModel.transform.transformType);
      const response = await this.configService.getCacheStatus(request);
      if(response){
        this.cacheStatus=response.cacheStatus
      }

    }catch (err) {
      this.displayError('Failed to Update Node Status', 'Error', err);
    } finally {
      //sender.isGettingData.next(false);
    }
  }

  async purgeCache(): Promise<void> {
    this.showConfirmDialog(
      'Purge Cache',
      'This deletes this transformer\'s cached output. Anything downstream that needs it as input will be regenerated automatically the next time it\'s opened. Continue?',
      async (confirmed) => {
        if (!confirmed) { return; }
        try {
          const cachekeyRequest=await this.GetCacheKeyRequest(PreviewType.transOutput);
          const request = new CacheStatusRequest(
            cachekeyRequest.userId,
            cachekeyRequest.orgId,
            cachekeyRequest.refineryId,
            cachekeyRequest.nodeId,
            cachekeyRequest.transformId,
            cachekeyRequest.cacheKeyType);
          const response = await this.configService.purgeCache(request);
          if(response){
            this.cacheStatus=response.cacheStatus
          }
        }catch (err) {
          this.displayError('Failed to purge transformer cache', 'Error', err);
        }
      }
    );
  }
}
export enum PriorityStringType {
  action = 0,
  object = 1
}
export class PriorityStringTypeUtil {
  static toString(priorityStringType:PriorityStringType): string {
      switch (priorityStringType) {
          case PriorityStringType.action: return 'Action';
          case PriorityStringType.object: return 'Object';
      }
  }
}
